import React, { useEffect, useRef, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { TouchApp } from "@material-ui/icons";
import Brightness4 from "@material-ui/icons/Brightness4";
import PlanAnimation from "./PlanAnimation";
import { getPrimaryUrl } from "./helpers/utmHelper";
import logo from "../assets/images/Uclusion_Wordmark_Color.svg";

// J-Marketing-27: the old chat-vs-job comparison ("Plan in a tool designed for
// developers.") is replaced by a feature list synced to a multi-scene simulated-app
// animation (Q-Marketing-37 O-2). No section heading (Q-Marketing-42 O-4) — the
// shared-workspace framing line (T-Marketing-201, Q-Marketing-41 O-1) is now the
// page headline rendered by AboveTheFold (T-Marketing-211), and the button under
// the animation is the primary Try for Free CTA. Scenes, in play order:
//   C. the AI's own window listing AI-approved work over MCP (T-Marketing-205,
//      T-Marketing-209 — inside the AI window, not Uclusion) as a numbered pick
//      list (S-all-143); the human answers "1" and the job the next scene opens
//      starts, so the loop reads as one story
//   H. a human pokes the AI from an assigned job's right panel; Uclusion injects
//      "Start J-6" into the connected AI terminal and the agent loads the job
//      and begins work (T-all-2399)
//   B. asking a question inside an AI-generated option on that job, ending with
//      the question resolved (T-Marketing-204), modeled on the real question
//      anatomy in uclusion-blog question.png (C-Marketing-209): type chips, short
//      codes, action buttons, option tabs, approvals, nested question comments
//   A. AI files a suggestion, the human moves it to a bug (T-Marketing-203/202 —
//      one merged list line per T-Marketing-207)
//   E. back in the AI window, the human searches the workspace; uclusion export
//      fires and the found items land with their details inline, then a drill-in
//      shows the full Q-12 decision in the conversation (T-all-2325 per
//      Q-all-250 O-2 + S-1; conception per T-all-2332) — recall directly
//      precedes the reopen act so the story reads found it, then reopened it
//   D. the resolved question revisited and reopened, thread intact
//      (T-Marketing-206 per Q-Marketing-40 O-1) — same card as B, its own act so
//      the list order AI sees / Ask questions / AI suggests / Ask about past
//      decisions / Reopen anything matches the play order
//   F. "Traditional UI or tell your agent" (T-all-2347): the AI window again —
//      find_work returns empty, the AI offers to create the job from the
//      human's plain description, add_job fires, and a closing find_work
//      returns the job under the name the AI inferred itself. This is the
//      origin of the password-reset job the other scenes build on. Scene shape
//      per https://stage.uclusion.com/dd56682c-9920-417b-be46-7a30d41bc905/Q-all-266
//      O-3 (no task list — a human spelling out tasks would look laborious);
//      tool name confirmed in
//      https://stage.uclusion.com/dd56682c-9920-417b-be46-7a30d41bc905/Q-all-265.
//   I. one human stays in the Uclusion Inbox while AI-driven Marketing and
//      Engineering jobs ask for different kinds of help (J-Marketing-28,
//      Q-Marketing-58 O-1, Q-Marketing-59 O-1). The Marketing message is a
//      structured copy choice; the Engineering message is review feedback.
//      Both disappear from You able after the response, with no agent-chat cut.
//   G. human collaboration (T-Marketing-212): one teammate's job approval is
//      already visible; another collaborator opens the real approval wizard,
//      chooses a certainty, writes a reason, and submits. The final approval
//      list shows both people and the configured expiry (Q-Marketing-49 O-1).
// Same replica technique and palette as PlanAnimation so the page reads as one app.

const BLUE = '#2f80ed';
const TOPBAR = '#a9d4d9';
const BOARD = '#b8e2e5';
const INK = '#1f2933';
const CARD_BORDER = '#cfe1e1';
const CHIP_TEAL = { text: '#347079', border: '#7fb6bd' };
const CHIP_RED = { text: '#c8362f', border: '#e85757' };
const CHIP_YELLOW = { text: '#975a16', border: '#e0b25e' };
const CHIP_GREEN = { text: '#276749', border: '#68b787' };

// Comment type colors from uclusion_web_ui's chip vocabulary.
const TYPE_COLOR = {
    question: '#2f80ed',
    suggestion: '#b96f00',
    bug: '#c8362f',
    note: '#5a6572',
};

// Scene timeline. One flat list of phases; each phase key is prefixed with its
// scene letter so highlights, stage swaps, and window chrome derive from the key.
// T-all-2329: five features auto-advancing was too fast to grasp, so playback is
// per-feature — the top feature's scene plays on load and loops until the
// customer clicks another feature line, which plays and loops that scene. The
// list keeps the old play order (c, h, b, a, e, d — f, i, t, and g appended later)
// so the story still reads top-to-bottom for anyone who clicks through in order.
const TIMELINE = [
    { key: 'c-chat', ms: 2300 },     // the human types; the AI's tool call fires
    { key: 'c-list', ms: 2400 },     // the AI-approved list lands all at once
    { key: 'c-pick', ms: 3000 },     // the human answers "1"; the AI starts that job
    { key: 'h-job', ms: 1800 },      // the assigned job and its right-panel actions
    { key: 'h-move', ms: 950 },      // cursor glides to Poke AI
    { key: 'h-click', ms: 450 },     // click
    { key: 'h-sent', ms: 1200 },     // Uclusion confirms delivery to the terminal
    // C-Marketing-227: stay on the Uclusion side after the poke — the working
    // AI asks something back, the human answers it right here, and only then
    // does the scene cut to the AI side to show the response landing.
    { key: 'h-question', ms: 2400 }, // the AI's question card arrives on the job
    { key: 'h-reply', ms: 3100 },    // the human types the answer
    // No cursor for the submit: the user tabs to Reply and hits enter, so the
    // button gets a focus ring, then activates, with the pointer nowhere near.
    { key: 'h-replytab', ms: 500 },  // tab: focus ring lands on Reply
    { key: 'h-replyenter', ms: 400 },// enter: Responded J-6 is sent for you
    { key: 'h-replysent', ms: 1200 },// second delivery receipt
    { key: 'h-terminal', ms: 2400 }, // cut to the AI side: Responded J-6 arrives
    { key: 'h-continue', ms: 3800 }, // the agent reads the reply and keeps going
    { key: 'b-question', ms: 2600 }, // question with an AI-authored approval
    { key: 'b-nested', ms: 3800 },   // the human asks inside the option; AI replies
    { key: 'b-approved', ms: 2000 }, // the informed human adds their own approval
    { key: 'b-design', ms: 2600 },   // the supported choice becomes a Design note
    { key: 'b-resolved', ms: 1800 }, // the AI resolves the answered question itself
    { key: 'a-view', ms: 1400 },     // the view, before the AI acts
    { key: 'a-suggest', ms: 2400 },  // AI suggestion card slides in
    { key: 'a-move', ms: 1100 },     // cursor glides to the Move button
    { key: 'a-open', ms: 450 },      // click; menu opens
    { key: 'a-menu', ms: 1200 },     // cursor glides to "To bug"
    { key: 'a-click', ms: 450 },     // click
    { key: 'a-bug', ms: 2200 },      // card re-renders as a bug; Bugs badge ticks
    { key: 'e-ask', ms: 2900 },      // the human asks a workspace search; export runs
    { key: 'e-results', ms: 5500 },  // found items land one by one, details inline
    { key: 'e-drill', ms: 2400 },    // the human drills into one result
    { key: 'e-detail', ms: 6000 },   // the full decision shows in the conversation
    { key: 'd-resolved', ms: 1600 }, // back on the resolved question
    { key: 'd-omove', ms: 900 },     // cursor glides to Reopen
    { key: 'd-oclick', ms: 450 },    // click
    { key: 'd-reopen', ms: 2400 },   // reopened — the whole thread is still there
    { key: 'f-ask', ms: 2300 },      // the human asks; find_work fires on an empty workspace
    { key: 'f-empty', ms: 2600 },    // nothing to do — the AI offers to create the job
    { key: 'f-describe', ms: 4600 }, // the human only describes the problem
    { key: 'f-create', ms: 3400 },   // add_job fires; the AI names the job itself
    { key: 'f-found', ms: 6000 },    // find_work returns the job under its new title —
                                     // the reveal, held long enough to actually read
    { key: 'i-inbox', ms: 1800 },    // Marketing and Engineering both need the human
    { key: 'i-marketing-move', ms: 900 },
    { key: 'i-marketing-open', ms: 450 },
    { key: 'i-marketing-question', ms: 1400 },
    { key: 'i-choice-move', ms: 850 },
    { key: 'i-choice-click', ms: 450 }, // the option expands; voting is the answer
    { key: 'i-vote', ms: 1100 },
    { key: 'i-certainty-move', ms: 750 },
    { key: 'i-certainty-click', ms: 450 },
    { key: 'i-choice-reason', ms: 2300 },
    { key: 'i-approve-move', ms: 750 },
    { key: 'i-approve-click', ms: 450 },
    { key: 'i-marketing-done', ms: 1300 }, // answered; Responded is sent automatically
    { key: 'i-engineering-move', ms: 900 },
    { key: 'i-engineering-open', ms: 450 },
    { key: 'i-review', ms: 1200 },
    { key: 'i-reply-move', ms: 800 },
    { key: 'i-reply-click', ms: 450 },
    { key: 'i-feedback', ms: 3000 },
    { key: 'i-submit-move', ms: 850 },
    { key: 'i-submit-click', ms: 450 },
    { key: 'i-done', ms: 3800 },     // both agents have their durable response
    { key: 't-ask', ms: 3600 },      // the human asks the AI for last week's usage
    { key: 't-report', ms: 7500 },   // six per-job totals and bucket breakdowns land
    { key: 'g-job', ms: 1600 },      // one teammate has already approved the job
    { key: 'g-addmove', ms: 900 },   // cursor moves to Add approval
    { key: 'g-addclick', ms: 450 },  // click opens the inline approval wizard
    { key: 'g-wizard', ms: 1300 },   // real wizard prompt and certainty choices
    { key: 'g-certmove', ms: 800 },  // cursor moves to Certain
    { key: 'g-certclick', ms: 450 }, // choose the certainty score
    { key: 'g-reason', ms: 3300 },   // the human types the approval reason
    { key: 'g-submitmove', ms: 850 },// cursor moves to Approve job
    { key: 'g-submitclick', ms: 450 },// submit
    { key: 'g-done', ms: 4200 },     // both expiring, reasoned approvals are visible
];

// T-Marketing-207: suggestions are one line covering both bugs and features.
// Each feature owns one scene; clicking its line plays that scene (T-all-2329).
const FEATURES = [
    {
        // The former deeper-walkthrough video, promoted to the top of the list.
        id: 'ideas',
        label: 'First easily enter ideas in built in stages',
        scene: 'plan',
    },
    {
        id: 'approved',
        label: 'AI lists available work in the right stage',
        scene: 'c',
    },
    {
        // T-Marketing-217: the line sells the standing listener, not one poke.
        id: 'poke-ai',
        label: 'AI monitors for pokes, responses, and new tasks',
        scene: 'h',
    },
    {
        id: 'question-options',
        label: 'Beyond plan mode: better UI and designs',
        scene: 'b',
    },
    {
        id: 'suggests',
        label: 'AI suggests potential bugs or features',
        scene: 'a',
    },
    {
        id: 'recall',
        label: 'AI searches the whole workspace',
        scene: 'e',
    },
    {
        id: 'persist',
        label: 'Reopen anything: planning persists',
        scene: 'd',
    },
    {
        id: 'tell-agent',
        label: 'Traditional UI or tell your agent',
        scene: 'f',
    },
    {
        id: 'multi-agent-inbox',
        label: 'Easily steer multiple agents',
        scene: 'i',
    },
    {
        id: 'token-usage',
        label: 'Track tokens by job and use',
        scene: 't',
    },
    {
        id: 'human-collaboration',
        label: 'Add humans to any collaboration',
        scene: 'g',
    },
];

const Container = tw.div`pb-16 px-4 md:px-8 lg:px-16`;
const WideWrap = tw.div`max-w-8xl mx-auto`;

const PrimaryButtonBase = tw.button`px-10 py-5 bg-blue-500 text-white border-0 hover:bg-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-200 font-sans transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl active:scale-95`;
const PrimaryButton = tw(
    PrimaryButtonBase,
)`text-xl font-semibold inline-block rounded-xl shadow-lg tracking-wide uppercase text-sm`;

const SyncGrid = tw.div`mt-4 grid grid-cols-1 xl:grid-cols-3 gap-6 xl:gap-8 items-start`;

/* ---- Feature list (left, narrow) -------------------------------------- */
const FeatureCol = styled.div`
  ${tw`xl:col-span-1 flex flex-col gap-2`}
  /* T-Marketing-207: the first line sits at the old second line's height so the
     highlights track the window's content area, not its chrome (desktop only). */
  @media (min-width: 1280px) {
    padding-top: 49px;
  }
`;
const FeatureItem = styled.div`
  ${tw`font-sans rounded-md`}
  font-size: 15px;
  line-height: 1.35;
  padding: 10px 14px;
  cursor: pointer;
  border-left: 3px solid ${(p) => (p.$active ? BLUE : '#d7dee2')};
  color: ${(p) => (p.$active ? INK : '#5f6b76')};
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  background: ${(p) => (p.$active ? '#ffffff' : 'transparent')};
  box-shadow: ${(p) => (p.$active ? '0 2px 8px rgba(31, 41, 51, 0.12)' : 'none')};
  transition: all 0.35s ease;
  &:hover {
    border-left-color: ${BLUE};
    color: ${INK};
  }
`;
// On mobile only the playing video's explainer shows: one caption in the
// active-line style. The scenes rotate on their own since there is no list to
// click; a tap on the caption skips ahead.
const MobileCaption = styled.div`
  ${tw`font-sans rounded-md`}
  font-size: 15px;
  line-height: 1.35;
  padding: 10px 14px;
  cursor: pointer;
  border-left: 3px solid ${BLUE};
  color: ${INK};
  font-weight: 700;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(31, 41, 51, 0.12);
`;
// PlanAnimation runs on its own ~9.5s clock with no phase machinery, so the
// mobile rotation gives it a fixed slot long enough for one full pass.
const PLAN_MOBILE_MS = 12000;

/* ---- Animation window (right, wide) ------------------------------------ */
const AnimCol = tw.div`xl:col-span-2`;
const Backdrop = styled.div`
  ${tw`rounded-2xl p-2 sm:p-4`}
  background: linear-gradient(135deg, #d6eeee 0%, #e6f4f4 60%, #eef7f6 100%);
`;
const AppWindow = styled.div`
  ${tw`rounded-xl overflow-hidden relative`}
  border: 1px solid #bcd9d9;
  box-shadow: 0 24px 50px -16px rgba(31, 41, 51, 0.4);
  font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
  background: ${(p) => (p.$chat ? '#ffffff' : BOARD)};
  /* B-Marketing-7: keep the cursor (z 40) and move menu (z 30) inside this
     window's stacking context — the header's Blog/Documentation iframe overlay
     sits at z-index 5 and must cover them. */
  isolation: isolate;
`;
const TopBar = styled.div`
  ${tw`flex items-center`}
  height: 44px;
  padding: 0 12px;
  background: ${TOPBAR};
  border-bottom: 1px solid #93c2c2;
  gap: 10px;
`;
const LogoImg = styled.img`
  width: 96px;
  height: auto;
  flex-shrink: 0;
`;
const TopBarSpacer = styled.div`
  flex: 1;
`;
// Scene G carries the full app chrome copied from PlanAnimation's top bar —
// Home link, workspace search, Next message, help/theme icons — so the job
// page reads like the real product (Q-Marketing-54 O-4).
const HomeLink = styled.span`
  ${tw`font-semibold`}
  color: ${BLUE};
  font-size: 12px;
  flex-shrink: 0;
  @media (max-width: 720px) {
    display: none;
  }
`;
const SearchPill = styled.div`
  ${tw`flex items-center rounded-full`}
  background: #eef8f8;
  border: 1px solid #bcd9d9;
  color: #6b8a8a;
  font-size: 12px;
  padding: 5px 12px;
  gap: 7px;
  width: 300px;
  max-width: 34%;
  min-width: 0;
  flex-shrink: 1;
  @media (max-width: 720px) {
    display: none;
  }
`;
const NextMsg = styled.div`
  ${tw`inline-flex items-center rounded-full font-semibold`}
  background: #eef8f8;
  border: 1px solid #bcd9d9;
  color: #3a5f5e;
  font-size: 12px;
  padding: 4px 12px;
  gap: 6px;
  flex-shrink: 0;
  span { color: ${BLUE}; }
  @media (max-width: 720px) {
    display: none;
  }
`;
const TopBarRight = styled.div`
  ${tw`inline-flex items-center`}
  gap: 10px;
  flex-shrink: 0;
  @media (max-width: 720px) {
    gap: 6px;
  }
`;
const TopIcon = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full`}
  width: 22px;
  height: 22px;
  color: #3a5f5e;
  font-size: 14px;
  @media (max-width: 720px) {
    display: none;
  }
`;
const ModeIcon = styled(Brightness4)`
  color: #3a5f5e;
  cursor: pointer;
  font-size: 20px !important;
`;
const SearchGlyph = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="11" cy="11" r="7" stroke="#6b8a8a" strokeWidth="2" />
        <path d="M21 21l-4.3-4.3" stroke="#6b8a8a" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
// Both collaborators live in the window chrome the whole time — the quiet visual
// backup to the framing line (T-Marketing-201).
const HeadAvatar = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full font-bold text-white`}
  width: 24px;
  height: 24px;
  font-size: 9px;
  background: ${(p) => p.bg || '#64748b'};
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #93c2c2;
  margin-left: ${(p) => (p.overlap ? '-7px' : '0')};
`;

// T-Marketing-209: scene C swaps the whole chrome to the AI agent's own window —
// the same caricature the old comparison panel used.
const ChatChrome = styled.div`
  ${tw`flex items-center`}
  height: 44px;
  padding: 0 14px;
  background: #faf9f5;
  border-bottom: 1px solid #ece9e2;
  gap: 8px;
`;
const ClaudeMark = styled.span`
  ${tw`inline-flex items-center justify-center rounded`}
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  background: #d97757;
  color: #fff;
  font-size: 13px;
  line-height: 1;
`;
const ChatTitle = tw.span`font-sans text-sm font-medium text-gray-700`;

const TabStrip = styled.div`
  ${tw`flex items-center`}
  gap: 20px;
  background: #eaf6f6;
  border-bottom: 1px solid #bcd9d9;
  padding: 0 16px;
`;
const Tab = styled.div`
  ${tw`flex items-center font-semibold`}
  font-size: 12.5px;
  gap: 6px;
  padding: 10px 0 8px;
  color: ${(p) => (p.active ? INK : '#5b8482')};
  border-bottom: 2px solid ${(p) => (p.active ? BLUE : 'transparent')};
  margin-bottom: -1px;
  white-space: nowrap;
`;
const NewBadge = styled.span`
  ${tw`inline-flex items-center rounded-full font-bold`}
  font-size: 10px;
  padding: 1px 7px;
  color: ${(p) => p.text || CHIP_TEAL.text};
  background: #ffffff;
  border: 1.5px solid ${(p) => p.border || CHIP_TEAL.border};
  &.tick {
    animation: badge-pop 0.5s ease;
  }
  @keyframes badge-pop {
    0% { transform: scale(1); }
    45% { transform: scale(1.45); }
    100% { transform: scale(1); }
  }
`;

// Every scene stays mounted invisibly, stacked into one grid cell, so the stage
// is always as tall as the tallest scene and the window height never jumps.
const Stage = styled.div`
  position: relative;
`;
const HeightGhost = styled.div`
  visibility: hidden;
  display: grid;
  & > * {
    grid-area: 1 / 1;
  }
`;
const LiveScene = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(p) => (p.$chat ? '#ffffff' : 'transparent')};
  animation: scene-in 0.35s ease both;
  @keyframes scene-in {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;
const SceneBody = styled.div`
  padding: 14px 16px 16px;
`;

/* ---- Real comment-card anatomy (per question.png / uclusion_web_ui) ---- */
const Card = styled.div`
  ${tw`rounded-lg bg-white`}
  border: 1px solid ${CARD_BORDER};
  box-shadow: 0 1px 2px rgba(31, 41, 51, 0.05);
  padding: 10px 12px;
  position: relative;
`;
const CardHead = styled.div`
  ${tw`flex items-center flex-wrap`}
  gap: 6px;
  margin-bottom: 6px;
`;
// Outlined comment-type chip: white fill, colored border and text, like the
// app's "Question" / "Suggestion" chips.
const TypeChip = styled.span`
  ${tw`inline-flex items-center font-sans font-semibold rounded-full`}
  font-size: 10.5px;
  color: ${(p) => p.$color};
  background: #ffffff;
  border: 1.5px solid ${(p) => p.$color};
  padding: 1px 9px;
`;
const ShortCode = styled.span`
  ${tw`inline-flex items-center font-sans font-semibold`}
  font-size: 11px;
  color: ${BLUE};
  gap: 3px;
`;
const HeadMeta = styled.span`
  ${tw`font-sans ml-auto`}
  font-size: 10.5px;
  color: #6b8a8a;
`;
const AiAvatar = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full font-bold text-white`}
  width: 18px;
  height: 18px;
  font-size: 8px;
  flex-shrink: 0;
  background: ${(p) => p.bg || BLUE};
`;
const ByLine = styled.span`
  ${tw`inline-flex items-center font-sans font-semibold`}
  font-size: 11.5px;
  color: ${INK};
  gap: 5px;
`;
const CardTitle = styled.div`
  color: ${INK};
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.3;
`;
// The app's outlined pill action buttons (Reply / Move / Resolve / Reopen...).
const BtnRow = styled.div`
  ${tw`flex items-center flex-wrap`}
  gap: 8px;
  margin-top: 9px;
`;
const PillBtn = styled.span`
  ${tw`inline-flex items-center font-sans font-semibold rounded-full`}
  font-size: 11px;
  color: ${(p) => (p.$primary ? BLUE : '#37474f')};
  background: #ffffff;
  border: 1.5px solid ${(p) => (p.$primary ? BLUE : '#b0bec5')};
  padding: 3px 12px;
  gap: 5px;
  white-space: nowrap;
`;
const SevChip = styled(TypeChip)`
  text-transform: uppercase;
  animation: chip-pop 0.4s ease both;
  @keyframes chip-pop {
    from { transform: scale(0.4); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }
`;
const NoteText = styled.div`
  color: #4a5568;
  font-size: 11.5px;
  line-height: 1.35;
`;

const enterCss = `
  animation: card-in 0.4s ease both;
  @keyframes card-in {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;
const EnteringCard = styled(Card)`
  ${enterCss}
`;
const Pulse = styled.div`
  border-radius: 8px;
  animation: pulse-ring 1.1s ease 0.35s;
  @keyframes pulse-ring {
    0% { box-shadow: 0 0 0 0 rgba(47, 128, 237, 0.45); }
    100% { box-shadow: 0 0 0 12px rgba(47, 128, 237, 0); }
  }
`;

/* ---- Scene A: move menu ------------------------------------------------ */
const Menu = styled.div`
  ${tw`absolute rounded-md bg-white`}
  z-index: 30;
  border: 0.5px solid grey;
  box-shadow: 0 8px 20px rgba(31, 41, 51, 0.25);
  padding: 4px 0;
  animation: scene-in 0.2s ease both;
`;
const MenuItem = styled.div`
  ${tw`font-sans`}
  font-size: 12px;
  color: ${INK};
  padding: 6px 16px;
  white-space: nowrap;
  background: ${(p) => (p.$hover ? '#ebf8ff' : 'transparent')};
`;

/* ---- Scene B: question anatomy (per question.png) ----------------------- */
const OptTabs = styled.div`
  ${tw`flex items-center`}
  gap: 18px;
  border-bottom: 1px solid #dde7e7;
  margin-top: 10px;
`;
const OptTab = styled.div`
  ${tw`flex items-center font-sans font-semibold`}
  font-size: 11px;
  gap: 5px;
  padding: 6px 0 5px;
  color: ${(p) => (p.active ? INK : '#5b8482')};
  border-bottom: 2px solid ${(p) => (p.active ? BLUE : 'transparent')};
  margin-bottom: -1px;
`;
const OptionRow = styled.div`
  ${tw`flex items-center rounded border border-gray-300 mt-2`}
  background: ${(p) => (p.$expanded ? '#ebf8ff' : '#ffffff')};
  border-color: ${(p) => (p.$expanded ? '#b4d0d8' : undefined)};
  padding: 5px 9px;
  gap: 7px;
`;
const OptionName = styled.span`
  ${tw`font-sans font-semibold`}
  font-size: 12px;
  color: ${INK};
`;
// Like the real OptionListItem: the name keeps its room, the description
// yields and ellipsizes.
const OptionDesc = styled.span`
  ${tw`font-sans`}
  font-size: 11px;
  color: #6b8a8a;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ThumbCount = styled.span`
  ${tw`font-sans`}
  font-size: 10px;
  color: #5b8482;
  flex-shrink: 0;
`;
const Chevron = styled.span`
  ${tw`ml-auto`}
  color: #5b8482;
  font-size: 11px;
  flex-shrink: 0;
`;
// The expanded option's inner panel: approvals, then comments.
const OptExpand = styled.div`
  margin: 6px 0 4px 12px;
  padding: 8px 10px 8px 12px;
  border-left: 2px solid #cbd5e0;
`;
const SectionLabel = styled.div`
  ${tw`font-sans font-bold`}
  font-size: 11px;
  color: ${INK};
  margin-bottom: 5px;
`;
const ApprovalLine = styled.div`
  ${tw`flex items-center`}
  gap: 7px;
  margin-bottom: 8px;
`;
const CertaintyChip = styled.span`
  ${tw`inline-flex items-center font-sans font-bold rounded-full`}
  font-size: 10px;
  color: ${CHIP_GREEN.text};
  background: #ffffff;
  border: 1.5px solid ${CHIP_GREEN.border};
  padding: 1px 9px;
  /* Chips never break internally at narrow widths - the row's title wraps instead. */
  white-space: nowrap;
  flex-shrink: 0;
`;
const NestedComment = styled.div`
  ${tw`rounded-md bg-white`}
  border: 1px solid #dde7e7;
  padding: 7px 9px;
  ${enterCss}
`;
const NestedText = styled.div`
  ${tw`font-sans`}
  font-size: 12px;
  color: ${INK};
  margin-top: 4px;
`;
// The reply appears automatically once the human finishes typing the question
// (the 2.2s delay covers the typing run), but is instantly present when the card
// remounts for the reopen act — a missing reply would contradict the reopened
// thread being intact.
const AiReply = styled.div`
  ${tw`flex items-start rounded`}
  margin-top: 7px;
  margin-left: 10px;
  font-size: 11.5px;
  font-family: ui-sans-serif, system-ui, sans-serif;
  color: #37474f;
  background: #f1f5f7;
  padding: 5px 8px;
  gap: 6px;
  animation: card-in 0.4s ease ${(p) => (p.$stagger ? '2.2s' : '0s')} both;
`;
const ResolvedStrip = styled.div`
  ${tw`flex items-center rounded font-sans`}
  margin-top: 9px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${(p) => (p.$reopened ? '#2c5282' : CHIP_GREEN.text)};
  background: ${(p) => (p.$reopened ? '#ebf8ff' : '#c6f6d5')};
  padding: 5px 10px;
  gap: 8px;
  ${enterCss}
`;

/* ---- Scene G: human job approvals (T-Marketing-212) -------------------- */
// This mirrors PlanningInvestible's Approvals section, Voting rows, and the
// inline JobApproveStep wizard from uclusion_web_ui. The compact layout keeps
// the real information hierarchy legible inside the storefront animation.
const ApprovalHeader = styled.div`
  ${tw`flex items-center flex-wrap`}
  gap: 8px;
  margin: 12px 0 8px;
`;
const ApprovalHeading = styled.div`
  ${tw`font-sans font-bold`}
  color: ${INK};
  font-size: 16px;
`;
const IconAction = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full`}
  width: 25px;
  height: 25px;
  color: #526d72;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  background: ${(p) => (p.$active ? '#d8eff0' : 'transparent')};
`;
const ExpiryConfig = styled.div`
  ${tw`font-sans ml-auto rounded-full`}
  color: #526d72;
  border: 1px solid #91bfc2;
  background: #eaf6f6;
  font-size: 10.5px;
  padding: 3px 9px;
  white-space: nowrap;
`;
const ApprovalEntry = styled.div`
  ${tw`rounded-lg bg-white`}
  border: 1px solid ${CARD_BORDER};
  padding: 9px 11px;
  margin-top: 8px;
  box-shadow: 0 1px 2px rgba(31, 41, 51, 0.05);
  ${(p) => p.$entering && enterCss}
`;
const ApprovalTop = styled.div`
  ${tw`flex items-center flex-wrap`}
  gap: 8px;
`;
const PersonName = styled.span`
  ${tw`font-sans font-medium`}
  color: ${INK};
  font-size: 12.5px;
`;
const Expires = styled.span`
  ${tw`font-sans`}
  color: #37474f;
  font-size: 11.5px;
  margin-left: auto;
  white-space: nowrap;
`;
const ApprovalReason = styled.div`
  ${tw`font-sans`}
  color: #37474f;
  font-size: 12px;
  line-height: 1.35;
  margin: 7px 0 0;
`;
const WizardCard = styled(Card)`
  padding: 12px 14px;
  ${enterCss}
`;
const WizardTitle = styled.div`
  ${tw`font-sans font-bold`}
  color: ${INK};
  font-size: 14px;
  margin-bottom: 10px;
`;
const CertaintyChoices = styled.div`
  ${tw`flex items-center flex-wrap`}
  gap: 7px;
  padding-bottom: 10px;
  border-bottom: 1px solid #d8e4e4;
`;
const CertaintyChoice = styled.span`
  ${tw`inline-flex items-center rounded-full font-sans font-semibold`}
  font-size: 10.5px;
  color: ${(p) => (p.$selected ? CHIP_GREEN.text : '#596b72')};
  border: 1.5px solid ${(p) => (p.$selected ? CHIP_GREEN.border : '#aebdc1')};
  background: ${(p) => (p.$selected ? '#f0fff4' : '#ffffff')};
  box-shadow: ${(p) => (p.$selected ? `0 0 0 2px ${CHIP_GREEN.border}33` : 'none')};
  padding: 3px 9px;
  white-space: nowrap;
`;
const ReasonLabel = styled.div`
  ${tw`font-sans font-semibold`}
  color: ${INK};
  font-size: 11.5px;
  margin: 10px 0 5px;
`;
const ReasonEditor = styled.div`
  ${tw`rounded-md bg-white font-sans`}
  min-height: 50px;
  border: 1px solid #aebdc1;
  color: #37474f;
  font-size: 12px;
  padding: 8px 9px;
`;
const WizardActions = styled.div`
  ${tw`flex items-center justify-end`}
  gap: 8px;
  margin-top: 10px;
`;
const ApproveButton = styled.span`
  ${tw`inline-flex items-center rounded-full font-sans font-bold`}
  color: #ffffff;
  background: ${BLUE};
  font-size: 11px;
  padding: 5px 14px;
`;
// Q-Marketing-54 O-4: the scene mirrors the real light-mode job page from the
// supplied approval screenshot — tab strip, created-by meta row, plain title
// card (type chip and stage live in the right panel, as in the app), Approvals
// anatomy with trash and Reply, and the wizard's full five-certainty scale.
// The 14-day expiry surfaces stay per Q-Marketing-49 O-1.
const MetaRow = styled.div`
  ${tw`flex items-center font-sans`}
  gap: 6px;
  font-size: 11px;
  color: #37474f;
  margin-bottom: 10px;
`;
const MetaEdit = styled.span`
  margin-left: auto;
  color: #526d72;
  white-space: nowrap;
`;
const NarrowHideTab = styled(Tab)`
  @media (max-width: 470px) {
    display: none;
  }
`;
const ReplyLink = styled.div`
  ${tw`font-sans font-medium`}
  color: #78909c;
  font-size: 11px;
  margin-top: 7px;
`;
const TrashGlyph = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 6h16M9 6V4h6v2M7 6l1 14h8l1-14"
            stroke={CHIP_RED.border} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const CopyGlyph = styled.span`
  color: ${BLUE};
  font-size: 11px;
  line-height: 1;
`;
const StageValue = styled.div`
  ${tw`font-sans font-medium`}
  color: ${INK};
  font-size: 11.5px;
  margin-bottom: 10px;
`;
const CollabRow = styled.div`
  ${tw`flex items-center`}
`;
const WizardJobTitle = styled.div`
  ${tw`font-sans font-bold`}
  color: ${INK};
  font-size: 12.5px;
  margin: -2px 0 10px;
`;

/* ---- Scene I: one Inbox for several AI-driven work streams ------------ */
// The scene mirrors the current Messages -> You able information hierarchy.
// It deliberately groups work by view instead of inventing named AI workers:
// J-all-385 enables several sessions to hand material decisions back through
// Uclusion, but it does not add per-agent identity or assignment semantics.
const InboxLayout = styled.div`
  display: grid;
  grid-template-columns: 128px minmax(0, 1fr);
  min-height: 340px;
  background: #f5fbfb;
  text-align: left;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
    min-height: 390px;
  }
`;
const InboxSidebar = styled.div`
  padding: 15px 11px;
  background: #e6f4f4;
  border-right: 1px solid #bcd9d9;

  @media (max-width: 560px) {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 8px 10px;
    border-right: 0;
    border-bottom: 1px solid #bcd9d9;
  }
`;
const InboxNavHeading = styled.div`
  ${tw`font-sans font-black`}
  color: ${INK};
  font-size: 16px;
  margin: 0 7px 13px;

  @media (max-width: 560px) {
    margin: 0 5px 0 0;
    font-size: 13px;
  }
`;
const InboxNavLabel = styled.div`
  ${tw`font-sans font-bold`}
  color: #526d72;
  font-size: 10px;
  letter-spacing: 0.06em;
  margin: 0 7px 5px;
  text-transform: uppercase;

  @media (max-width: 560px) {
    display: none;
  }
`;
const InboxNavItem = styled.div`
  ${tw`flex items-center rounded-md font-sans font-semibold`}
  color: ${(p) => (p.$active ? INK : '#5b7377')};
  background: ${(p) => (p.$active ? '#ffffff' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? '#c9dddd' : 'transparent')};
  font-size: 11.5px;
  padding: 6px 7px;
  margin-top: 3px;
  gap: 6px;

  @media (max-width: 560px) {
    margin-top: 0;
    padding: 4px 8px;
    font-size: 10.5px;
  }
`;
const InboxNavCount = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full font-bold ml-auto`}
  min-width: 18px;
  height: 18px;
  color: #ffffff;
  background: ${BLUE};
  font-size: 9px;
  padding: 0 5px;
`;
const InboxMain = styled.div`
  min-width: 0;
  padding: 12px 14px 15px;

  @media (max-width: 560px) {
    padding: 10px 10px 13px;
  }
`;
const InboxToolbar = styled.div`
  ${tw`flex items-center`}
  gap: 8px;
  margin-bottom: 9px;
`;
const InboxHeading = styled.div`
  ${tw`font-sans font-black`}
  color: ${INK};
  font-size: 16px;
`;
const InboxPosition = styled.span`
  ${tw`font-sans ml-auto`}
  color: #6b8a8a;
  font-size: 10.5px;
  white-space: nowrap;
`;
const InboxArrow = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full`}
  width: 23px;
  height: 23px;
  color: ${BLUE};
  border: 1px solid #b4d0d8;
  background: #ffffff;
  font-size: 13px;
`;
const InboxGroup = styled.div`
  margin-top: 8px;
`;
const InboxGroupHeader = styled.div`
  ${tw`font-sans font-semibold`}
  color: #527073;
  background: #e8f4f4;
  border: 1px solid #c7dddd;
  border-radius: 6px 6px 0 0;
  font-size: 10.5px;
  line-height: 1.25;
  padding: 5px 9px;
`;
const InboxMessage = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  background: ${(p) => (p.$active ? '#ebf8ff' : '#ffffff')};
  border: 1px solid ${(p) => (p.$active ? '#8bbce8' : CARD_BORDER)};
  border-top: 0;
  border-radius: 0 0 6px 6px;
  padding: 8px 9px;
  box-shadow: 0 1px 2px rgba(31, 41, 51, 0.04);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
`;
const InboxMessageCopy = styled.div`
  min-width: 0;
`;
const InboxMessageTitle = styled.div`
  ${tw`font-sans font-bold`}
  color: ${INK};
  font-size: 11.5px;
  line-height: 1.25;
`;
const InboxMessageText = styled.div`
  ${tw`font-sans`}
  color: #60777a;
  font-size: 10.5px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const InboxChevron = styled.span`
  color: ${BLUE};
  font-size: 15px;
  line-height: 1;
`;
const InboxWizard = styled(Card)`
  padding: 11px 12px;
  ${enterCss}
`;
const InboxWizardPrompt = styled.div`
  ${tw`font-sans font-black`}
  color: ${INK};
  font-size: 14px;
  margin-bottom: 7px;
`;
const InboxJobLine = styled.div`
  ${tw`flex items-center flex-wrap font-sans`}
  color: #526d72;
  font-size: 10.5px;
  gap: 6px;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #d8e4e4;
`;
const InboxChoices = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
  margin-top: 9px;

  @media (max-width: 440px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const InboxChoice = styled.div`
  ${tw`rounded-md font-sans`}
  color: ${INK};
  background: ${(p) => (p.$selected ? '#f0fff4' : '#ffffff')};
  border: 1.5px solid ${(p) => (p.$selected ? CHIP_GREEN.border : '#b4c7ca')};
  box-shadow: ${(p) => (p.$selected ? '0 0 0 2px rgba(104, 183, 135, 0.17)' : 'none')};
  font-size: 10.5px;
  font-weight: 650;
  line-height: 1.25;
  padding: 7px 8px;
  cursor: pointer;
`;
const InboxReviewBody = styled.div`
  ${tw`rounded-md font-sans`}
  color: #37474f;
  background: #f4f8f8;
  border: 1px solid #d7e5e5;
  font-size: 11px;
  line-height: 1.35;
  padding: 8px 9px;
  margin-top: 7px;
`;
const InboxPrimaryButton = styled.span`
  ${tw`inline-flex items-center rounded-full font-sans font-bold`}
  color: #ffffff;
  background: ${(p) => (p.$pressed ? '#2368b8' : BLUE)};
  box-shadow: ${(p) => (p.$pressed ? '0 0 0 3px rgba(47, 128, 237, 0.24)' : 'none')};
  font-size: 10.5px;
  padding: 5px 13px;
`;
const InboxSuccess = styled.div`
  ${tw`flex items-center rounded-md font-sans font-semibold`}
  color: ${CHIP_GREEN.text};
  background: #f0fff4;
  border: 1px solid #a6d8b8;
  font-size: 10.5px;
  line-height: 1.3;
  padding: 6px 9px;
  gap: 7px;
  margin-bottom: 8px;
  ${enterCss}
`;
const InboxCaughtUp = styled.div`
  ${tw`flex flex-col items-center justify-center rounded-lg font-sans text-center`}
  min-height: 205px;
  color: #526d72;
  background: #ffffff;
  border: 1px dashed #b4d0d8;
  font-size: 11.5px;
  padding: 20px;
  gap: 7px;
`;
const InboxDoneMark = styled.div`
  ${tw`inline-flex items-center justify-center rounded-full font-black`}
  width: 34px;
  height: 34px;
  color: #ffffff;
  background: ${CHIP_GREEN.border};
  font-size: 18px;
`;

/* ---- Scene H: poke assigned work into the AI terminal (T-all-2399) ------ */
const PokeJobLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(155px, 0.85fr);
  gap: 12px;
  align-items: stretch;
  text-align: left;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const JobSummary = styled(Card)`
  padding: 12px 13px;
`;
const JobDescription = styled.div`
  ${tw`font-sans`}
  color: #4a5568;
  font-size: 11.5px;
  line-height: 1.4;
  margin-top: 6px;
`;
const AssigneeRow = styled.div`
  ${tw`flex items-center rounded-md`}
  gap: 8px;
  margin-top: 12px;
  padding: 7px 9px;
  border: 1px solid #d7e5e5;
  background: #f6fbfb;
`;
const AssigneeText = styled.div`
  ${tw`font-sans`}
  min-width: 0;
  flex: 1;
  color: ${INK};
  font-size: 11.5px;
  line-height: 1.25;
`;
const AssignedChip = styled.span`
  ${tw`font-sans font-semibold rounded-full`}
  color: ${CHIP_GREEN.text};
  background: #ffffff;
  border: 1.5px solid ${CHIP_GREEN.border};
  font-size: 9.5px;
  padding: 2px 7px;
  white-space: nowrap;
`;
const TaskPreview = styled.div`
  ${tw`font-sans`}
  margin-top: 11px;
  color: #53656c;
  font-size: 10.5px;
`;
const TaskPreviewRow = styled.div`
  ${tw`flex items-center`}
  gap: 7px;
  margin-top: 5px;
`;
const TaskDot = styled.span`
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  border-radius: 9999px;
  background: ${(p) => (p.$active ? BLUE : '#9fb6b8')};
`;
const JobRightPanel = styled.div`
  ${tw`rounded-lg bg-white flex flex-col`}
  min-width: 0;
  border: 1px solid ${CARD_BORDER};
  box-shadow: 0 1px 2px rgba(31, 41, 51, 0.05);
  padding: 11px 12px;
`;
const PanelHeading = styled.div`
  ${tw`font-sans font-bold`}
  color: ${INK};
  font-size: 11.5px;
  margin-bottom: 7px;
`;
const TicketLinkPill = styled.div`
  ${tw`inline-flex items-center self-start rounded-full font-sans font-semibold`}
  max-width: 100%;
  color: ${BLUE};
  border: 1px solid #b4d0d8;
  background: #ffffff;
  font-size: 10.5px;
  padding: 4px 10px;
  gap: 5px;
  white-space: nowrap;
`;
// Mirrors the app's SpinningIconLabelButton geometry and TouchApp icon: compact,
// black outlined pill, directly below the ticket-link pill.
const PokeAiButton = styled.div`
  ${tw`inline-flex items-center self-start rounded-full font-sans font-semibold`}
  color: #1f2933;
  border: 1.5px solid #7b8b91;
  background: ${(p) => (p.$pressed ? '#d8eff0' : '#ffffff')};
  box-shadow: ${(p) => (p.$pressed ? '0 0 0 3px rgba(47, 128, 237, 0.18)' : 'none')};
  font-size: 11px;
  line-height: 1;
  padding: 5px 11px;
  gap: 6px;
  margin-top: 9px;
  white-space: nowrap;
  transition: background 0.2s ease, box-shadow 0.2s ease;

  svg {
    width: 15px;
    height: 15px;
    color: #1f2933;
  }
`;
const DeliveryReceipt = styled.div`
  ${tw`font-sans font-semibold`}
  color: ${CHIP_GREEN.text};
  font-size: 10.5px;
  line-height: 1.3;
  padding-top: 11px;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? '0' : '4px')});
  transition: opacity 0.25s ease, transform 0.25s ease;
`;

// C-Marketing-227: the AI's question arrives on the job while you watch, and
// gets answered in place — evidence the poke landed, with no cut away.
const AiQuestionCard = styled(Card)`
  margin-top: 10px;
  ${enterCss}
`;
// The reply submits by keyboard (tab, then enter), so the button shows a focus
// ring and a pressed flash instead of receiving the cursor.
const ReplyBtn = styled(PillBtn)`
  border-color: ${(p) => (p.$focus ? BLUE : undefined)};
  box-shadow: ${(p) => (p.$focus ? '0 0 0 3px rgba(47, 128, 237, 0.25)' : 'none')};
  background: ${(p) => (p.$pressed ? '#d8eff0' : '#ffffff')};
  transition: box-shadow 0.15s ease, background 0.15s ease;
`;

const TerminalBody = styled.div`
  ${tw`font-sans`}
  height: 100%;
  box-sizing: border-box;
  min-height: 244px;
  padding: 15px 16px 17px;
  color: #d9e2ec;
  background: #17212b;
  text-align: left;
  ${enterCss}

  @media (max-width: 560px) {
    min-height: 278px;
  }
`;
const TerminalStatus = styled.div`
  ${tw`flex items-center`}
  color: #9fb3c8;
  font-size: 10.5px;
  letter-spacing: 0.02em;
  gap: 7px;
  margin-bottom: 13px;
`;
const ConnectedDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background: #68b787;
  box-shadow: 0 0 0 3px rgba(104, 183, 135, 0.15);
`;
const IncomingCommand = styled.div`
  ${tw`rounded-md`}
  border: 1px solid #526a7a;
  background: #202e3a;
  padding: 9px 11px;
  margin-bottom: 11px;
  ${enterCss}
`;
const CommandOrigin = styled.div`
  ${tw`font-sans font-semibold`}
  color: #8fb8d8;
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 5px;
`;
const CommandText = styled.code`
  color: #ffffff;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  font-weight: 650;
`;
const TerminalLine = styled.div`
  color: #b9c8d6;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
  line-height: 1.5;
  margin-top: 7px;
  ${enterCss}
`;
const TerminalTool = styled.div`
  ${tw`inline-flex items-center rounded-md`}
  color: #c4d2df;
  border: 1px solid #3e5363;
  background: #1d2935;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 10.5px;
  padding: 6px 9px;
  gap: 7px;
  margin-top: 9px;
  ${enterCss}
`;
const WorkingState = styled.div`
  ${tw`flex items-center rounded-md font-sans font-semibold`}
  color: #dff7e8;
  border: 1px solid #47765a;
  background: #20352a;
  font-size: 11.5px;
  padding: 7px 10px;
  gap: 8px;
  margin-top: 11px;
  ${enterCss}
`;
const WorkingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: #68b787;
  animation: working-pulse 1.25s ease-in-out infinite;

  @keyframes working-pulse {
    0%, 100% { opacity: 0.45; transform: scale(0.85); }
    50% { opacity: 1; transform: scale(1.15); }
  }
`;

/* ---- Scene C: the AI agent window (T-Marketing-209) --------------------- */
const ChatBody = styled.div`
  ${tw`flex flex-col`}
  background: #ffffff;
  padding: 14px 16px 16px;
  gap: 10px;
`;
const YouBubble = styled.div`
  ${tw`font-sans rounded-2xl self-end`}
  font-size: 12.5px;
  color: #2d3748;
  background: #ececec;
  padding: 6px 12px;
  max-width: 85%;
`;
const ToolChip = styled.div`
  ${tw`flex items-start rounded-md self-start`}
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #5a6572;
  background: #faf9f5;
  border: 1px solid #e7e3da;
  padding: 6px 9px;
  gap: 7px;
  ${enterCss}
  /* The AI's tool call fires only after the human finishes typing, so the delay
     must cover the phase's typing run (~55ms per character plus a beat). */
  animation-delay: ${(p) => p.$delay || '1.6s'};
`;
const ToolIcon = styled.span`
  color: #d97757;
  line-height: 1.4;
`;
const AiLine = styled.div`
  ${tw`flex items-start`}
  gap: 8px;
  ${enterCss}
`;
const AiText = styled.div`
  ${tw`font-sans`}
  font-size: 12.5px;
  color: ${INK};
  line-height: 1.4;
`;
const WorkRow = styled.div`
  ${tw`flex items-center rounded-md`}
  border: 1.5px solid ${(p) => (p.$picked ? BLUE : '#e7e3da')};
  background: ${(p) => (p.$picked ? '#ebf8ff' : '#fdfcfa')};
  font-size: 12px;
  padding: 5px 9px;
  gap: 8px;
  margin-left: 28px;
  transition: border-color 0.3s ease, background 0.3s ease;
  ${enterCss}
  animation-delay: ${(p) => p.$delay || '0s'};
`;
const WorkCheck = styled.span`
  color: ${CHIP_GREEN.text};
  font-weight: 700;
`;
// find_work presents choices as a numbered pick list; the number is the handle
// the human answers with.
const WorkNum = styled.span`
  ${tw`font-sans font-bold`}
  font-size: 12px;
  color: #5b8482;
`;
const WorkTitle = styled.span`
  ${tw`font-sans font-semibold`}
  font-size: 12px;
  color: ${INK};
`;

/* ---- Cursor ------------------------------------------------------------ */
const CursorWrap = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 40;
  pointer-events: none;
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transform: translate(${(p) => p.x}px, ${(p) => p.y}px);
  transition: transform ${(p) => (p.instant ? '0s' : `${p.duration}ms`)} cubic-bezier(0.45, 0, 0.25, 1),
    opacity 0.25s ease;
`;
const CursorRipple = styled.span`
  position: absolute;
  left: -3px;
  top: -3px;
  width: 26px;
  height: 26px;
  border-radius: 9999px;
  border: 2px solid ${BLUE};
  opacity: 0;
  ${(p) => p.clicking && `
    animation: ripple 0.45s ease-out;
  `}
  @keyframes ripple {
    from { opacity: 0.7; transform: scale(0.3); }
    to   { opacity: 0; transform: scale(1.6); }
  }
`;
const PointerGlyph = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 3l14 8-6 1.5L9 19 5 3z" fill="#1f2933" stroke="#fff" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
);

/* ---- Typed human input --------------------------------------------------- */
// Only AI output appears on its own; anything the human "says" is typed out
// with a caret. `active` types the text during its phase; anywhere else (later
// phases, the height ghost, the reopen act) the full text renders instantly.
const TYPE_MS = 55;
const Caret = styled.span`
  display: inline-block;
  width: 1.5px;
  height: 11px;
  background: ${INK};
  margin-left: 1px;
  vertical-align: -1px;
  animation: caret-blink 0.9s step-end infinite;
  @keyframes caret-blink {
    50% { opacity: 0; }
  }
`;
const TypedText = ({ text, active }) => {
    const [count, setCount] = useState(active ? 0 : text.length);
    useEffect(() => {
        if (!active) {
            setCount(text.length);
            return undefined;
        }
        setCount(0);
        const interval = setInterval(() => {
            setCount((prev) => {
                if (prev >= text.length) {
                    clearInterval(interval);
                    return prev;
                }
                return prev + 1;
            });
        }, TYPE_MS);
        return () => clearInterval(interval);
    }, [active, text]);
    const typing = active && count < text.length;
    return <>{text.slice(0, count)}{typing && <Caret />}</>;
};
const LinkGlyph = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M10 14a5 5 0 007.07 0l3-3A5 5 0 0013 4l-1.5 1.5M14 10a5 5 0 00-7.07 0l-3 3A5 5 0 0011 20l1.5-1.5"
            stroke={BLUE} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);

/* ---- Scenes ------------------------------------------------------------ */
const SceneA = ({ phase, moveBtnRef, menuItemRef }) => {
    const showSuggest = phase !== 'a-view';
    const menuOpen = phase === 'a-menu' || phase === 'a-click';
    const isBug = phase === 'a-bug';
    const bugCount = isBug ? 4 : 3;
    return (
        <>
            <TabStrip>
                <Tab>
                    Bugs
                    <NewBadge key={bugCount} className={isBug ? 'tick' : undefined}
                        text={isBug ? CHIP_RED.text : undefined} border={isBug ? CHIP_RED.border : undefined}>
                        {bugCount} open
                    </NewBadge>
                </Tab>
                <Tab active>Notes / Discussion</Tab>
            </TabStrip>
            <SceneBody>
                <Card style={{ marginBottom: 10 }}>
                    <CardHead style={{ marginBottom: 4 }}>
                        <TypeChip $color={TYPE_COLOR.note}>Note</TypeChip>
                        <HeadMeta>Created 2 days ago.</HeadMeta>
                    </CardHead>
                    <NoteText>
                        Focus: the mobile release. AI has permission to read this view and the backlog.
                    </NoteText>
                </Card>
                {showSuggest && (
                    <Pulse>
                        <EnteringCard>
                            <CardHead>
                                {isBug
                                    ? <TypeChip $color={TYPE_COLOR.bug}>Bug</TypeChip>
                                    : <TypeChip $color={TYPE_COLOR.suggestion}>Suggestion</TypeChip>}
                                {isBug && <SevChip $color={CHIP_YELLOW.text} style={{ borderColor: CHIP_YELLOW.border }}>Normal</SevChip>}
                                <ShortCode><LinkGlyph />{isBug ? 'B-14' : 'S-9'}</ShortCode>
                                <ByLine><AiAvatar>AI</AiAvatar> AI</ByLine>
                                <HeadMeta>Created just now.</HeadMeta>
                            </CardHead>
                            <CardTitle>Mobile popups should be full-screen dialogs under 600px</CardTitle>
                            <BtnRow>
                                <PillBtn>↩ Reply</PillBtn>
                                <PillBtn ref={moveBtnRef}>▲ Move</PillBtn>
                                <PillBtn>✓ Resolve</PillBtn>
                            </BtnRow>
                            {menuOpen && (
                                <Menu style={{ left: 90, bottom: -62 }}>
                                    <MenuItem ref={menuItemRef} $hover={phase === 'a-click'}>To bug</MenuItem>
                                    <MenuItem>To task</MenuItem>
                                </Menu>
                            )}
                        </EnteringCard>
                    </Pulse>
                )}
            </SceneBody>
        </>
    );
};

// Rendered for both the question act (b-*) and the later reopen act (d-*).
// The anatomy follows uclusion-blog question.png: header metas with edit
// affordance, the full action-button row, option rows that ellipsize their
// description like the real OptionListItem, an AI-authored approval with its
// reason, and the nested question as a full comment card with its own short
// code and Reply / Delegate buttons. Once the AI answers, the human adds an
// informed approval and the resulting supported choice becomes a Design note.
const SceneB = ({ phase, reopenRef, questionId }) => {
    const showNested = phase !== 'b-question';
    const showHumanApproval = !['b-question', 'b-nested'].includes(phase);
    const showDesign = ['b-design', 'b-resolved', 'd-resolved', 'd-omove',
        'd-oclick', 'd-reopen'].includes(phase);
    const resolved = ['b-resolved', 'd-resolved', 'd-omove', 'd-oclick'].includes(phase);
    const reopened = phase === 'd-reopen';
    return (
        <SceneBody>
            <Card style={{ marginBottom: 10, padding: '6px 10px', background: '#ebf8ff', borderColor: '#b4d0d8' }}>
                <ByLine style={{ fontSize: 12.5 }}>
                    <TypeChip $color={BLUE}>Job</TypeChip> Add password reset to the login page
                </ByLine>
            </Card>
            <Pulse key={reopened ? 'reopened' : 'open'}>
                <Card id={questionId}>
                    <CardHead>
                        <TypeChip $color={TYPE_COLOR.question}>Question</TypeChip>
                        <ShortCode><LinkGlyph />Q-12</ShortCode>
                        <ByLine><AiAvatar>AI</AiAvatar> AI</ByLine>
                        <HeadMeta>Created 5 minutes ago. Updated just now. ✎</HeadMeta>
                    </CardHead>
                    <CardTitle>How should the reset link expire?</CardTitle>
                    <BtnRow>
                        <PillBtn>↩ Reply</PillBtn>
                        <PillBtn>+ Add option</PillBtn>
                        <PillBtn>⚙ Configure voting</PillBtn>
                        <PillBtn>▲ Move</PillBtn>
                        {resolved
                            ? <PillBtn ref={reopenRef} $primary>↻ Reopen</PillBtn>
                            : <PillBtn>✓ Delegate</PillBtn>}
                    </BtnRow>
                    {(resolved || reopened) && (
                        <ResolvedStrip $reopened={reopened}>
                            {reopened ? 'Reopened. The whole thread is still here' : '✓ Resolved by AI'}
                        </ResolvedStrip>
                    )}
                    <OptTabs>
                        <OptTab active>👍 Approvable Options</OptTab>
                        <OptTab>⊘ Proposed Options</OptTab>
                    </OptTabs>
                    <OptionRow $expanded>
                        <OptionName>15 min, single-use</OptionName>
                        <OptionDesc>Expires fast so a leaked link is worthless…</OptionDesc>
                        <ThumbCount>👍 {showHumanApproval ? 2 : 1}</ThumbCount>
                        <AiAvatar>AI</AiAvatar>
                        <Chevron>▲</Chevron>
                    </OptionRow>
                    <OptExpand>
                        <NoteText style={{ fontSize: 10.5, marginBottom: 6 }}>Created by AI.</NoteText>
                        <SectionLabel>Approvals</SectionLabel>
                        <ApprovalLine>
                            <CertaintyChip>Certain</CertaintyChip>
                            <AiAvatar>AI</AiAvatar>
                            <NoteText style={{ fontSize: 11 }}>
                                <strong>AI approval:</strong> Fifteen minutes limits exposure. Single-use prevents replay.
                            </NoteText>
                            <Chevron>▼</Chevron>
                        </ApprovalLine>
                        {showHumanApproval && (
                            <ApprovalLine>
                                <CertaintyChip>Certain</CertaintyChip>
                                <AiAvatar bg="#4c5b8a">DI</AiAvatar>
                                <NoteText style={{ fontSize: 11 }}>
                                    <strong>David approved:</strong> Fifteen minutes is fine. Users can request another link.
                                </NoteText>
                                <Chevron>▼</Chevron>
                            </ApprovalLine>
                        )}
                        {showNested && (
                            <NestedComment>
                                <CardHead style={{ marginBottom: 0 }}>
                                    <TypeChip $color={TYPE_COLOR.question}>Question</TypeChip>
                                    <ByLine><AiAvatar bg="#4c5b8a">DI</AiAvatar> David Israel</ByLine>
                                    <HeadMeta>Created just now. <span style={{ color: BLUE }}>Q-2</span></HeadMeta>
                                </CardHead>
                                <NestedText>
                                    <TypedText text="Does 15 minutes hurt UX?" active={phase === 'b-nested'} />
                                </NestedText>
                                <AiReply $stagger={phase === 'b-nested'}>
                                    <AiAvatar>AI</AiAvatar>
                                    <span>A reset is rare, so users will not notice.</span>
                                </AiReply>
                            </NestedComment>
                        )}
                    </OptExpand>
                    <OptionRow>
                        <OptionName>1 hour, single-use</OptionName>
                        <OptionDesc>More forgiving for slow email delivery…</OptionDesc>
                        <AiAvatar>AI</AiAvatar>
                        <Chevron>▼</Chevron>
                    </OptionRow>
                </Card>
            </Pulse>
            {showDesign && (
                <EnteringCard style={{ marginTop: 10 }}>
                    <CardHead>
                        <TypeChip $color={TYPE_COLOR.note}>Design note</TypeChip>
                        <ShortCode><LinkGlyph />R-8</ShortCode>
                        <ByLine><AiAvatar>AI</AiAvatar> AI</ByLine>
                        <HeadMeta>Created just now.</HeadMeta>
                    </CardHead>
                    <CardTitle>
                        See <a href={`#${questionId}`} style={{ color: '#2c5282', textDecoration: 'underline' }}>
                            How should the link expire?
                        </a>
                    </CardTitle>
                </EnteringCard>
            )}
        </SceneBody>
    );
};

// The picked job is the one the question scene right after this opens — the
// human answering with its number and the AI starting it keeps the loop one
// story. The rows land together, like a tool result renders, just after the
// AI's line. S-all-143: find_work presents as the canonical numbered pick list
// ("choose from the following"), matching scene F's closing beat.
const APPROVED_JOBS = [
    { title: 'Add password reset to the login page', certainty: 'certainty 4/5', picked: true },
    { title: 'Rate-limit the reset endpoint', certainty: 'certainty 5/5' },
    { title: 'Send the reset email', certainty: 'certainty 4/5' },
];

const SceneC = ({ phase }) => {
    const picked = phase === 'c-pick';
    return (
        <ChatBody>
            <YouBubble>
                <TypedText text="What should I work on?" active={phase === 'c-chat'} />
            </YouBubble>
            <ToolChip>
                <ToolIcon>⏺</ToolIcon>
                <span>find_work · Uclusion MCP</span>
            </ToolChip>
            {phase !== 'c-chat' && (
                <>
                    <AiLine>
                        <ClaudeMark>✳</ClaudeMark>
                        <AiText>Three jobs are AI-approved. Choose from the following:</AiText>
                    </AiLine>
                    {APPROVED_JOBS.map((job, index) => (
                        <WorkRow key={job.title} $delay="0.3s" $picked={picked && job.picked}>
                            <WorkNum>{index + 1}.</WorkNum>
                            <WorkTitle>{job.title}</WorkTitle>
                            <CertaintyChip>{job.certainty}</CertaintyChip>
                        </WorkRow>
                    ))}
                    {picked && (
                        <>
                            <YouBubble>
                                <TypedText text="1" active={picked} />
                            </YouBubble>
                            <AiLine style={{ animationDelay: '0.5s' }}>
                                <ClaudeMark>✳</ClaudeMark>
                                <AiText>Starting: Add password reset to the login page.</AiText>
                            </AiLine>
                        </>
                    )}
                </>
            )}
        </ChatBody>
    );
};

// The search act (T-all-2325 per Q-all-250 O-2 + S-1): same AI window as scene C,
// but the tool that fires is the CLI export. T-all-2332: the AI has the whole
// workspace locally, so every found item carries its detail inline and the
// drill-in happens right in the conversation — Uclusion is a link away, not a
// required trip. Paced so each beat lands alone (T-all-2331). The found items
// are the other scenes' artifacts, so the search visibly spans the workspace.
const ResultRow = styled.div`
  ${tw`rounded-md`}
  border: 1.5px solid ${(p) => (p.$picked ? BLUE : '#e7e3da')};
  background: ${(p) => (p.$picked ? '#ebf8ff' : '#fdfcfa')};
  padding: 6px 10px;
  margin-left: 28px;
  transition: border-color 0.3s ease, background 0.3s ease;
  ${enterCss}
  animation-delay: ${(p) => p.$delay || '0s'};
`;
const ResultHead = styled.div`
  ${tw`flex items-center`}
  gap: 8px;
`;
const ResultDetail = styled.div`
  ${tw`font-sans`}
  font-size: 11.5px;
  color: #4a5568;
  margin-top: 3px;
`;

/* ---- Scene T: an export-driven token report (J-Marketing-29) ----------- */
const UsageList = styled.div`
  ${tw`flex flex-col`}
  gap: 5px;
  margin-left: 28px;

  @media (max-width: 560px) {
    margin-left: 0;
  }
`;
const UsageRow = styled.div`
  ${tw`rounded-md`}
  min-width: 0;
  border: 1px solid #e7e3da;
  background: #fdfcfa;
  padding: 5px 9px;
  ${enterCss}
  animation-delay: ${(p) => p.$delay || '0s'};
`;
const UsageHead = styled.div`
  ${tw`flex items-center`}
  min-width: 0;
  gap: 7px;
`;
const UsageTitle = styled.span`
  ${tw`font-sans font-semibold`}
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${INK};
  font-size: 11.5px;
`;
const UsageTotal = styled.span`
  ${tw`font-sans font-bold ml-auto`}
  flex-shrink: 0;
  white-space: nowrap;
  color: #2f6f6d;
  font-size: 11px;
`;
const UsageBuckets = styled.div`
  ${tw`font-sans`}
  min-width: 0;
  margin-top: 2px;
  color: #6b7280;
  font-size: 10.5px;
  line-height: 1.25;

  @media (max-width: 560px) {
    white-space: normal;
  }
`;
const FOUND_ITEMS = [
    { code: 'Q-12', chip: 'Question', color: TYPE_COLOR.question, status: 'Resolved',
        detail: '15 min single-use links won; 1 hour was rejected on leak risk.' },
    { code: 'J-7', chip: 'Job', color: BLUE, status: 'Approved 5/5',
        detail: 'Rate-limit the reset endpoint.' },
    { code: 'J-8', chip: 'Job', color: BLUE, status: 'Approved 4/5',
        detail: 'Send the reset email.' },
];

const SceneE = ({ phase }) => {
    const showResults = phase !== 'e-ask';
    const showDrill = ['e-drill', 'e-detail'].includes(phase);
    const showDetail = phase === 'e-detail';
    return (
        <ChatBody>
            <YouBubble>
                <TypedText text="What do we have on password reset?" active={phase === 'e-ask'} />
            </YouBubble>
            <ToolChip $delay="2.1s">
                <ToolIcon>⏺</ToolIcon>
                <span>uclusion export · CLI</span>
            </ToolChip>
            {showResults && (
                <>
                    <AiLine>
                        <ClaudeMark>✳</ClaudeMark>
                        <AiText>Three matches with details inline. Drill in on any:</AiText>
                    </AiLine>
                    {FOUND_ITEMS.map((item, index) => (
                        <ResultRow key={item.code} $delay={`${0.4 + index * 0.6}s`}
                            $picked={showDetail && item.code === 'Q-12'}>
                            <ResultHead>
                                <TypeChip $color={item.color}>{item.chip}</TypeChip>
                                <ShortCode><LinkGlyph />{item.code}</ShortCode>
                                <CertaintyChip>{item.status}</CertaintyChip>
                            </ResultHead>
                            <ResultDetail>{item.detail}</ResultDetail>
                        </ResultRow>
                    ))}
                </>
            )}
            {showDrill && (
                <YouBubble>
                    <TypedText text="Show me Q-12." active={phase === 'e-drill'} />
                </YouBubble>
            )}
            {showDetail && (
                <NestedComment>
                    <CardHead style={{ marginBottom: 0 }}>
                        <TypeChip $color={TYPE_COLOR.question}>Question</TypeChip>
                        <ShortCode><LinkGlyph />Q-12</ShortCode>
                        <CardTitle style={{ fontSize: 12 }}>How should the reset link expire?</CardTitle>
                        <HeadMeta>Open in Uclusion ↗</HeadMeta>
                    </CardHead>
                    <NestedText>
                        <WorkCheck>✓</WorkCheck> 15 min, single-use. Certain: &ldquo;Standard for resets.
                        Single-use matters more.&rdquo;
                    </NestedText>
                    <NestedText style={{ color: '#6b7a88' }}>
                        ✗ 1 hour, single-use. Rejected: forgiving for slow email, but lost on leak risk.
                    </NestedText>
                </NestedComment>
            )}
        </ChatBody>
    );
};

const USAGE_JOBS = [
    {
        code: 'J-21',
        title: 'Track agent token usage',
        total: '142k total',
        buckets: 'planning 26k · implementation 88k · testing 28k',
    },
    {
        code: 'J-18',
        title: 'Launch the landing page',
        total: '83k total',
        buckets: 'web searches 18k · source review 25k · copywriting 40k',
    },
    {
        code: 'J-14',
        title: 'Guard duplicate notifications',
        total: '76k total',
        buckets: 'planning 12k · implementation 48k · testing 16k',
    },
    {
        code: 'J-16',
        title: 'Add password reset',
        total: '68k total',
        buckets: 'planning 14k · implementation 38k · testing 16k',
    },
    {
        code: 'J-12',
        title: 'Search the workspace',
        total: '61k total',
        buckets: 'planning 12k · implementation 34k · testing 15k',
    },
    {
        code: 'J-9',
        title: 'Invite teammates',
        total: '49k total',
        buckets: 'planning 10k · implementation 29k · testing 10k',
    },
];

// The AI is the reporting layer: it reads the structured notes in the local
// workspace export, then calculates the cross-job view the human requested.
const SceneT = ({ phase }) => {
    const showReport = phase === 't-report';
    return (
        <ChatBody>
            <YouBubble>
                <TypedText text="Report token usage for last week by job and bucket."
                    active={phase === 't-ask'} />
            </YouBubble>
            <ToolChip $delay="3s">
                <ToolIcon>⏺</ToolIcon>
                <span>uclusion export · CLI</span>
            </ToolChip>
            {showReport && (
                <>
                    <AiLine>
                        <ClaudeMark>✳</ClaudeMark>
                        <AiText>Last week: 479k tokens across 6 jobs.</AiText>
                    </AiLine>
                    <UsageList>
                        {USAGE_JOBS.map((job, index) => (
                            <UsageRow key={job.code} $delay={`${0.25 + index * 0.35}s`}
                                data-testid="token-usage-job">
                                <UsageHead>
                                    <ShortCode><LinkGlyph />{job.code}</ShortCode>
                                    <UsageTitle>{job.title}</UsageTitle>
                                    <UsageTotal>{job.total}</UsageTotal>
                                </UsageHead>
                                <UsageBuckets>{job.buckets}</UsageBuckets>
                            </UsageRow>
                        ))}
                    </UsageList>
                </>
            )}
        </ChatBody>
    );
};

// The creation act (T-all-2347, shape per Q-all-266 O-3): find_work comes back
// empty, so the AI offers to create the job and the human only describes the
// problem — no form, no fields, no task list. add_job derives the job's name
// from that description, so the title only appears when the closing find_work
// returns it: the password-reset job every other scene builds on, born here.
const SceneF = ({ phase }) => {
    const showEmpty = phase !== 'f-ask';
    const showDescribe = !['f-ask', 'f-empty'].includes(phase);
    const showCreate = ['f-create', 'f-found'].includes(phase);
    const showFound = phase === 'f-found';
    return (
        <ChatBody>
            <YouBubble>
                <TypedText text="Anything for me to work on?" active={phase === 'f-ask'} />
            </YouBubble>
            <ToolChip>
                <ToolIcon>⏺</ToolIcon>
                <span>find_work · Uclusion MCP</span>
            </ToolChip>
            {showEmpty && (
                <AiLine>
                    <ClaudeMark>✳</ClaudeMark>
                    <AiText>Your work list is empty. Describe the problem and I&rsquo;ll create the job.</AiText>
                </AiLine>
            )}
            {showDescribe && (
                <YouBubble>
                    <TypedText text="Customers keep emailing support because they can't reset their passwords."
                        active={phase === 'f-describe'} />
                </YouBubble>
            )}
            {showCreate && (
                <>
                    <ToolChip $delay="0.1s">
                        <ToolIcon>⏺</ToolIcon>
                        <span>add_job · Uclusion MCP</span>
                    </ToolChip>
                    <AiLine style={{ animationDelay: '0.6s' }}>
                        <ClaudeMark>✳</ClaudeMark>
                        <AiText>Created <ShortCode><LinkGlyph />J-6</ShortCode>. I named it from your description.</AiText>
                    </AiLine>
                </>
            )}
            {showFound && (
                <>
                    <ToolChip $delay="0.1s">
                        <ToolIcon>⏺</ToolIcon>
                        <span>find_work · Uclusion MCP</span>
                    </ToolChip>
                    <AiLine style={{ animationDelay: '0.5s' }}>
                        <ClaudeMark>✳</ClaudeMark>
                        <AiText>Choose from the following or add another:</AiText>
                    </AiLine>
                    <WorkRow $delay="0.9s" $picked>
                        <WorkNum>1.</WorkNum>
                        <WorkTitle>Add password reset to the login page</WorkTitle>
                    </WorkRow>
                </>
            )}
        </ChatBody>
    );
};

// T-Marketing-212, Q-Marketing-49 O-1: the result is collaborative, not just
// a form demo. Samantha's approval is present at the start; David adds a second
// approval through the same certainty + reason wizard used by the app, and the
// final state shows both entries with the workspace's configured 14-day expiry.
const SceneG = ({ phase, addRef, certaintyRef, submitRef }) => {
    const wizardPhases = [
        'g-wizard', 'g-certmove', 'g-certclick', 'g-reason',
        'g-submitmove', 'g-submitclick',
    ];
    const showWizard = wizardPhases.includes(phase);
    const certaintySelected = ['g-certclick', 'g-reason', 'g-submitmove', 'g-submitclick'].includes(phase);
    const showReason = ['g-reason', 'g-submitmove', 'g-submitclick'].includes(phase);
    const done = phase === 'g-done';
    const addActive = phase === 'g-addclick';

    if (showWizard) {
        // The app's approval wizard is its own page: intro question, the job
        // being approved, the full five-certainty scale, then the reason editor.
        return (
            <SceneBody>
                <WizardCard>
                    <WizardTitle>How certain are you this job should be done?</WizardTitle>
                    <WizardJobTitle>Add password reset to the login page</WizardJobTitle>
                    <CertaintyChoices>
                        <CertaintyChoice>Uncertain</CertaintyChoice>
                        <CertaintyChoice>Somewhat Uncertain</CertaintyChoice>
                        <CertaintyChoice>Somewhat Certain</CertaintyChoice>
                        <CertaintyChoice ref={certaintyRef} $selected={certaintySelected}>Certain</CertaintyChoice>
                        <CertaintyChoice>Very Certain</CertaintyChoice>
                    </CertaintyChoices>
                    <ReasonLabel>Why do you approve this job?</ReasonLabel>
                    <ReasonEditor>
                        {showReason && (
                            <TypedText
                                text="Customers need this before the mobile launch."
                                active={phase === 'g-reason'}
                            />
                        )}
                    </ReasonEditor>
                    <NoteText style={{ fontSize: 10.5, marginTop: 6 }}>
                        ⚙ Approvals expire after 14 days
                    </NoteText>
                    <WizardActions>
                        <PillBtn>Cancel</PillBtn>
                        <ApproveButton ref={submitRef}>Approve job</ApproveButton>
                    </WizardActions>
                </WizardCard>
            </SceneBody>
        );
    }

    return (
        <>
            <TabStrip>
                <Tab active>Overview</Tab>
                <Tab>Open tasks <NewBadge text={CHIP_YELLOW.text} border={CHIP_YELLOW.border}>1 total</NewBadge></Tab>
                <Tab>Debatable</Tab>
                <NarrowHideTab>Notes</NarrowHideTab>
            </TabStrip>
            <SceneBody>
                <MetaRow>
                    Created by
                    <AiAvatar bg="#b45309">SD</AiAvatar>
                    Samantha Demo
                    <MetaEdit>Entered 3 days ago ✎</MetaEdit>
                </MetaRow>
                <PokeJobLayout>
                    <div>
                        <JobSummary>
                            <CardTitle style={{ fontSize: 15 }}>Add password reset to the login page</CardTitle>
                            <JobDescription>
                                Let signed-out customers request a secure, single-use reset link.
                            </JobDescription>
                        </JobSummary>
                        <ApprovalHeader>
                            <ApprovalHeading>Approvals</ApprovalHeading>
                            <IconAction ref={addRef} $active={addActive}>+</IconAction>
                            <IconAction style={{ fontSize: 14 }}>⌃</IconAction>
                            <ExpiryConfig>⚙ 14-day expiry</ExpiryConfig>
                        </ApprovalHeader>
                        <ApprovalEntry>
                            <ApprovalTop>
                                <CertaintyChip>Certain</CertaintyChip>
                                <AiAvatar bg="#b45309">SD</AiAvatar>
                                <PersonName>Samantha Demo</PersonName>
                                <Expires>Expires in 12+ days</Expires>
                                <TrashGlyph />
                            </ApprovalTop>
                            <ApprovalReason>Table stakes feature.</ApprovalReason>
                            <ReplyLink>Reply</ReplyLink>
                        </ApprovalEntry>
                        {done && (
                            <ApprovalEntry $entering>
                                <ApprovalTop>
                                    <CertaintyChip>Certain</CertaintyChip>
                                    <AiAvatar bg="#4c5b8a">DI</AiAvatar>
                                    <PersonName>David Israel</PersonName>
                                    <Expires>Expires in 13+ days</Expires>
                                    <TrashGlyph />
                                </ApprovalTop>
                                <ApprovalReason>Customers need this before the mobile launch.</ApprovalReason>
                                <ReplyLink>Reply</ReplyLink>
                            </ApprovalEntry>
                        )}
                    </div>
                    <JobRightPanel>
                        <TicketLinkPill>
                            <LinkGlyph />
                            J-6
                            <CopyGlyph>⧉</CopyGlyph>
                        </TicketLinkPill>
                        <PanelHeading style={{ marginTop: 11 }}>Stage</PanelHeading>
                        <StageValue>Approvable ⌄</StageValue>
                        <PanelHeading>Assigned</PanelHeading>
                        <CollabRow style={{ gap: 6, marginBottom: 10 }}>
                            <AiAvatar>AI</AiAvatar>
                            <PersonName style={{ fontSize: 11.5 }}>Your AI teammate</PersonName>
                        </CollabRow>
                        <PanelHeading>Collaborators</PanelHeading>
                        <CollabRow>
                            <HeadAvatar bg="#b45309">SD</HeadAvatar>
                            <HeadAvatar bg="#4c5b8a" overlap>DI</HeadAvatar>
                            <HeadAvatar bg={BLUE} overlap>AI</HeadAvatar>
                        </CollabRow>
                    </JobRightPanel>
                </PokeJobLayout>
            </SceneBody>
        </>
    );
};

// J-Marketing-28: a one-human / several-agent story shown entirely in the
// current Uclusion Inbox. Q-Marketing-58 O-1 selected two different structured
// handoffs; Q-Marketing-59 O-1 placed this as its own selectable scene directly
// before the existing add-a-human scene. View names, You able, and the wizard
// prompts mirror the live product. Completing each response removes its row and
// sends the waiting agent the durable Responded signal without visiting chat.
const SceneI = ({ phase, marketingRef, choiceRef, certaintyRef, marketingSubmitRef,
    engineeringRef, replyReportRef, submitRef }) => {
    const marketingList = ['i-inbox', 'i-marketing-move'].includes(phase);
    const marketingOpen = [
        'i-marketing-open', 'i-marketing-question', 'i-choice-move', 'i-choice-click',
    ].includes(phase);
    const choiceSelected = phase === 'i-choice-click';
    const voteOpen = [
        'i-vote', 'i-certainty-move', 'i-certainty-click', 'i-choice-reason',
        'i-approve-move', 'i-approve-click',
    ].includes(phase);
    const certaintySelected = [
        'i-certainty-click', 'i-choice-reason', 'i-approve-move', 'i-approve-click',
    ].includes(phase);
    const choiceReasonVisible = ['i-choice-reason', 'i-approve-move', 'i-approve-click'].includes(phase);
    const approvePressed = phase === 'i-approve-click';
    const engineeringList = ['i-marketing-done', 'i-engineering-move'].includes(phase);
    const engineeringReview = [
        'i-engineering-open', 'i-review', 'i-reply-move', 'i-reply-click',
    ].includes(phase);
    const engineeringReply = ['i-feedback', 'i-submit-move', 'i-submit-click'].includes(phase);
    const replyReportPressed = phase === 'i-reply-click';
    const reviewPressed = phase === 'i-submit-click';
    const done = phase === 'i-done';
    const remaining = done ? 0 : (marketingList || marketingOpen || voteOpen ? 2 : 1);

    return (
        <InboxLayout>
            <InboxSidebar>
                <InboxNavHeading>Inbox</InboxNavHeading>
                <InboxNavLabel>Messages</InboxNavLabel>
                <InboxNavItem $active>
                    You able
                    <InboxNavCount>{remaining}</InboxNavCount>
                </InboxNavItem>
                <InboxNavItem>Pokable</InboxNavItem>
            </InboxSidebar>
            <InboxMain>
                <InboxToolbar>
                    <InboxHeading>You able</InboxHeading>
                    <InboxPosition>
                        {done ? '0 messages' : `1 of ${remaining}`}
                    </InboxPosition>
                    {!done && (
                        <>
                            <InboxArrow>‹</InboxArrow>
                            <InboxArrow>›</InboxArrow>
                        </>
                    )}
                </InboxToolbar>

                {marketingList && (
                    <>
                        <InboxGroup>
                            <InboxGroupHeader>Workspace David Israel and view Marketing</InboxGroupHeader>
                            <InboxMessage ref={marketingRef} $active={phase === 'i-marketing-move'}>
                                <TypeChip $color={TYPE_COLOR.question}>Question</TypeChip>
                                <InboxMessageCopy>
                                    <InboxMessageTitle>Launch the multi-agent campaign</InboxMessageTitle>
                                    <InboxMessageText>Which landing-page headline should lead?</InboxMessageText>
                                </InboxMessageCopy>
                                <InboxChevron>›</InboxChevron>
                            </InboxMessage>
                        </InboxGroup>
                        <InboxGroup>
                            <InboxGroupHeader>Workspace David Israel and view Engineering</InboxGroupHeader>
                            <InboxMessage>
                                <TypeChip $color={CHIP_GREEN.text}>Please review</TypeChip>
                                <InboxMessageCopy>
                                    <InboxMessageTitle>Keep AI activity out of email</InboxMessageTitle>
                                    <InboxMessageText>Notification guard ready for review</InboxMessageText>
                                </InboxMessageCopy>
                                <InboxChevron>›</InboxChevron>
                            </InboxMessage>
                        </InboxGroup>
                    </>
                )}

                {marketingOpen && (
                    <InboxGroup>
                        <InboxGroupHeader>Workspace David Israel and view Marketing</InboxGroupHeader>
                        <InboxWizard>
                            <InboxWizardPrompt>Can you help answer?</InboxWizardPrompt>
                            <InboxJobLine>
                                <TypeChip $color={TYPE_COLOR.question}>Question</TypeChip>
                                <ShortCode><LinkGlyph />Q-Marketing-9</ShortCode>
                                for
                                <ShortCode><LinkGlyph />J-Marketing-12</ShortCode>
                            </InboxJobLine>
                            <CardTitle style={{ fontSize: 12.5 }}>
                                Which landing-page headline should lead?
                            </CardTitle>
                            <InboxChoices>
                                <InboxChoice ref={choiceRef} $selected={choiceSelected}>
                                    Steer AI work from one inbox
                                </InboxChoice>
                                <InboxChoice>
                                    Ship twice as fast with AI
                                </InboxChoice>
                            </InboxChoices>
                        </InboxWizard>
                    </InboxGroup>
                )}

                {voteOpen && (
                    <InboxGroup>
                        <InboxGroupHeader>Workspace David Israel and view Marketing</InboxGroupHeader>
                        <InboxWizard>
                            <InboxWizardPrompt>How certain are you of this option?</InboxWizardPrompt>
                            <InboxJobLine>
                                <TypeChip $color={TYPE_COLOR.question}>Option</TypeChip>
                                <ShortCode><LinkGlyph />O-1</ShortCode>
                                in
                                <ShortCode><LinkGlyph />Q-Marketing-9</ShortCode>
                            </InboxJobLine>
                            <CardTitle style={{ fontSize: 12.5 }}>Steer AI work from one inbox</CardTitle>
                            <CertaintyChoices style={{ marginTop: 9 }}>
                                <CertaintyChoice>Uncertain</CertaintyChoice>
                                <CertaintyChoice>Somewhat Uncertain</CertaintyChoice>
                                <CertaintyChoice>Somewhat Certain</CertaintyChoice>
                                <CertaintyChoice ref={certaintyRef} $selected={certaintySelected}>Certain</CertaintyChoice>
                                <CertaintyChoice>Very Certain</CertaintyChoice>
                            </CertaintyChoices>
                            {choiceReasonVisible && (
                                <>
                                    <ReasonLabel>Why do you approve this option?</ReasonLabel>
                                    <ReasonEditor style={{ minHeight: 36 }}>
                                        <TypedText text="Clearest outcome for a busy human."
                                            active={phase === 'i-choice-reason'} />
                                    </ReasonEditor>
                                </>
                            )}
                            <WizardActions>
                                <PillBtn>Block instead</PillBtn>
                                <InboxPrimaryButton ref={marketingSubmitRef} $pressed={approvePressed}>
                                    Approve option
                                </InboxPrimaryButton>
                            </WizardActions>
                        </InboxWizard>
                    </InboxGroup>
                )}

                {engineeringList && (
                    <>
                        <InboxSuccess>
                            ✓ Marketing answered · AI can keep working
                        </InboxSuccess>
                        <InboxGroup>
                            <InboxGroupHeader>Workspace David Israel and view Engineering</InboxGroupHeader>
                            <InboxMessage ref={engineeringRef} $active={phase === 'i-engineering-move'}>
                                <TypeChip $color={CHIP_GREEN.text}>Please review</TypeChip>
                                <InboxMessageCopy>
                                    <InboxMessageTitle>Keep AI activity out of email</InboxMessageTitle>
                                    <InboxMessageText>Notification guard ready for review</InboxMessageText>
                                </InboxMessageCopy>
                                <InboxChevron>›</InboxChevron>
                            </InboxMessage>
                        </InboxGroup>
                    </>
                )}

                {engineeringReview && (
                    <InboxGroup>
                        <InboxGroupHeader>Workspace David Israel and view Engineering</InboxGroupHeader>
                        <InboxWizard>
                            <InboxWizardPrompt>Will you review?</InboxWizardPrompt>
                            <InboxJobLine>
                                <TypeChip $color={CHIP_GREEN.text}>Please review</TypeChip>
                                <ShortCode><LinkGlyph />C-Engineering-16</ShortCode>
                                for
                                <ShortCode><LinkGlyph />J-Engineering-42</ShortCode>
                            </InboxJobLine>
                            <CardTitle style={{ fontSize: 12.5 }}>Notification guard ready for review</CardTitle>
                            <InboxReviewBody>
                                AI activity now stays in Uclusion without sending email. 104 tests pass.
                            </InboxReviewBody>
                            <WizardActions>
                                <PillBtn>Comment in job</PillBtn>
                                <InboxPrimaryButton ref={replyReportRef} $pressed={replyReportPressed}>
                                    Reply to report
                                </InboxPrimaryButton>
                            </WizardActions>
                        </InboxWizard>
                    </InboxGroup>
                )}

                {engineeringReply && (
                    <InboxGroup>
                        <InboxGroupHeader>Workspace David Israel and view Engineering</InboxGroupHeader>
                        <InboxWizard>
                            <InboxWizardPrompt>What is your reply?</InboxWizardPrompt>
                            <InboxJobLine>
                                <TypeChip $color={CHIP_GREEN.text}>Please review</TypeChip>
                                <ShortCode><LinkGlyph />C-Engineering-16</ShortCode>
                                for
                                <ShortCode><LinkGlyph />J-Engineering-42</ShortCode>
                            </InboxJobLine>
                            <InboxReviewBody>
                                Notification guard ready for review
                            </InboxReviewBody>
                            <ReasonEditor style={{ marginTop: 8, minHeight: 36 }}>
                                <TypedText text="Looks good. Ship it." active={phase === 'i-feedback'} />
                            </ReasonEditor>
                            <WizardActions>
                                <InboxPrimaryButton ref={submitRef} $pressed={reviewPressed}>
                                    Send
                                </InboxPrimaryButton>
                            </WizardActions>
                        </InboxWizard>
                    </InboxGroup>
                )}

                {done && (
                    <InboxCaughtUp>
                        <InboxDoneMark>✓</InboxDoneMark>
                        <strong>You able is empty.</strong>
                        <span>Marketing has its answer. Engineering has its review.</span>
                        <span>Both AI-driven jobs can keep moving.</span>
                    </InboxCaughtUp>
                )}
            </InboxMain>
        </InboxLayout>
    );
};

// T-all-2399 + C-Marketing-227: the click starts in the real job context, then
// the scene STAYS on the Uclusion side — the working AI asks a question back,
// the human answers it on the job, and only then does the cut to the AI side
// show the response landing over the Start history and work continuing.
const SceneH = ({ phase, pokeRef }) => {
    const showTerminal = ['h-terminal', 'h-continue'].includes(phase);
    const continuing = phase === 'h-continue';

    if (showTerminal) {
        return (
            <TerminalBody>
                <TerminalStatus>
                    <ConnectedDot />
                    Connected to Uclusion
                </TerminalStatus>
                <IncomingCommand>
                    <CommandOrigin>Incoming from Poke AI</CommandOrigin>
                    <CommandText>Start J-6</CommandText>
                </IncomingCommand>
                <TerminalLine>› Assignment received from Uclusion</TerminalLine>
                <TerminalTool>
                    <ToolIcon>⏺</ToolIcon>
                    <span>get_job · Uclusion MCP</span>
                </TerminalTool>
                <TerminalLine>› Loaded J-6 · Add password reset to the login page</TerminalLine>
                <TerminalLine>› Began work · asked Q-14 about the email template</TerminalLine>
                <IncomingCommand style={{ marginTop: 11, animationDelay: '0.9s' }}>
                    <CommandOrigin>Incoming from Poke AI</CommandOrigin>
                    <CommandText>Responded J-6</CommandText>
                </IncomingCommand>
                {continuing && (
                    <>
                        <TerminalTool>
                            <ToolIcon>⏺</ToolIcon>
                            <span>get_job · Uclusion MCP</span>
                        </TerminalTool>
                        <TerminalLine>› Read your reply · reusing the standard template</TerminalLine>
                        <WorkingState>
                            <WorkingDot />
                            Continuing work on J-6
                        </WorkingState>
                    </>
                )}
            </TerminalBody>
        );
    }

    const pressed = phase === 'h-click';
    const sent = ['h-sent', 'h-question', 'h-reply', 'h-replytab', 'h-replyenter', 'h-replysent'].includes(phase);
    const asked = ['h-question', 'h-reply', 'h-replytab', 'h-replyenter', 'h-replysent'].includes(phase);
    const replying = ['h-reply', 'h-replytab', 'h-replyenter', 'h-replysent'].includes(phase);
    const replyFocus = ['h-replytab', 'h-replyenter'].includes(phase);
    const replyPressed = phase === 'h-replyenter';
    const respSent = phase === 'h-replysent';
    return (
        <>
            <TabStrip>
                <Tab active>Job</Tab>
                <Tab>Tasks <NewBadge>2 open</NewBadge></Tab>
                <Tab>Discussion</Tab>
            </TabStrip>
            <SceneBody>
                <PokeJobLayout>
                    <div>
                        <JobSummary>
                            <CardHead>
                                <TypeChip $color={BLUE}>Job</TypeChip>
                                <ShortCode><LinkGlyph />J-6</ShortCode>
                                <HeadMeta>Doable</HeadMeta>
                            </CardHead>
                            <CardTitle>Add password reset to the login page</CardTitle>
                            <JobDescription>
                                Let signed-out customers request a secure, single-use reset link.
                            </JobDescription>
                            <AssigneeRow>
                                <AiAvatar>AI</AiAvatar>
                                <AssigneeText>
                                    <strong>Your AI teammate</strong><br />
                                    Assigned this job
                                </AssigneeText>
                                <AssignedChip>Assigned</AssignedChip>
                            </AssigneeRow>
                            <TaskPreview>
                                <TaskPreviewRow>
                                    <TaskDot $active />
                                    Build the reset endpoint
                                </TaskPreviewRow>
                                <TaskPreviewRow>
                                    <TaskDot />
                                    Add the login-page flow
                                </TaskPreviewRow>
                            </TaskPreview>
                        </JobSummary>
                        {asked && (
                            <AiQuestionCard>
                                <CardHead style={{ marginBottom: 4 }}>
                                    <TypeChip $color={TYPE_COLOR.question}>Question</TypeChip>
                                    <ShortCode><LinkGlyph />Q-14</ShortCode>
                                    <AiAvatar>AI</AiAvatar>
                                    <HeadMeta>Created just now.</HeadMeta>
                                </CardHead>
                                <CardTitle style={{ fontSize: 12.5 }}>
                                    Should the reset email reuse the standard template?
                                </CardTitle>
                                {replying && (
                                    <>
                                        <ReasonEditor style={{ marginTop: 8, minHeight: 34 }}>
                                            <TypedText text="Yes, reuse the standard template."
                                                active={phase === 'h-reply'} />
                                        </ReasonEditor>
                                        <BtnRow>
                                            <ReplyBtn $primary $focus={replyFocus} $pressed={replyPressed}>↩ Reply</ReplyBtn>
                                        </BtnRow>
                                    </>
                                )}
                            </AiQuestionCard>
                        )}
                    </div>
                    <JobRightPanel>
                        <PanelHeading>Ticket link</PanelHeading>
                        <TicketLinkPill>
                            <LinkGlyph />
                            J-6
                        </TicketLinkPill>
                        <PokeAiButton ref={pokeRef} $pressed={pressed}
                            title="Send Start J-6 to your connected AI terminal">
                            <TouchApp />
                            Poke AI
                        </PokeAiButton>
                        <DeliveryReceipt $visible={sent}>
                            ✓ Sent Start J-6 to your AI terminal
                        </DeliveryReceipt>
                        <DeliveryReceipt $visible={respSent} style={{ paddingTop: 7 }}>
                            ✓ Sent Responded J-6 to your AI terminal
                        </DeliveryReceipt>
                    </JobRightPanel>
                </PokeJobLayout>
            </SceneBody>
        </>
    );
};

/* ---- Component ---------------------------------------------------------- */
const AiCollaborationFeature = ({ isMobile }) => {
    const appRef = useRef(null);
    const moveBtnRef = useRef(null);
    const menuItemRef = useRef(null);
    const reopenRef = useRef(null);
    const pokeAiRef = useRef(null);
    const approvalAddRef = useRef(null);
    const approvalCertaintyRef = useRef(null);
    const approvalSubmitRef = useRef(null);
    const inboxMarketingRef = useRef(null);
    const inboxChoiceRef = useRef(null);
    const inboxCertaintyRef = useRef(null);
    const inboxMarketingSubmitRef = useRef(null);
    const inboxEngineeringRef = useRef(null);
    const inboxReplyReportRef = useRef(null);
    const inboxSubmitRef = useRef(null);
    // T-all-2329: playback is per-feature. The selected feature's scene phases loop
    // until another feature line is clicked; loopCount keys the live scene so each
    // pass replays its entering animations from a clean mount.
    const [selectedId, setSelectedId] = useState(FEATURES[0].id);
    const [phaseIndex, setPhaseIndex] = useState(0);
    const [loopCount, setLoopCount] = useState(0);
    const [cursor, setCursor] = useState({ x: 40, y: 60, instant: true, shown: false, duration: 1000 });

    const selectedScene = FEATURES.find((feature) => feature.id === selectedId).scene;
    // The ideas entry plays PlanAnimation, which loops on its own clock and swaps
    // in for the whole app window; the phase machinery only drives replica scenes.
    const isPlan = selectedScene === 'plan';
    const scenePhases = isPlan ? [] : TIMELINE.filter((entry) => entry.key.startsWith(selectedScene));
    const phase = isPlan ? null : scenePhases[phaseIndex].key;
    const scene = isPlan ? null : phase[0];

    // Mobile rotation: with only the current caption visible there is no list
    // to click, so each scene hands off to the next feature when its loop ends.
    const advanceFeature = () => {
        const idx = FEATURES.findIndex((feature) => feature.id === selectedId);
        setSelectedId(FEATURES[(idx + 1) % FEATURES.length].id);
        setPhaseIndex(0);
        setLoopCount(0);
    };

    useEffect(() => {
        if (isPlan) {
            return undefined;
        }
        const timer = setTimeout(() => {
            if (isMobile && phaseIndex + 1 >= scenePhases.length) {
                advanceFeature();
                return;
            }
            setPhaseIndex((prev) => {
                if (prev + 1 >= scenePhases.length) {
                    setLoopCount((count) => count + 1);
                    return 0;
                }
                return prev + 1;
            });
        }, scenePhases[phaseIndex].ms);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phaseIndex, selectedId, loopCount]);

    useEffect(() => {
        if (!isMobile || !isPlan) {
            return undefined;
        }
        const timer = setTimeout(advanceFeature, PLAN_MOBILE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, isPlan, selectedId]);

    const selectFeature = (feature) => {
        if (feature.id !== selectedId) {
            setSelectedId(feature.id);
            setPhaseIndex(0);
            setLoopCount(0);
        }
    };

    // Cursor choreography. Targets are measured after the phase's DOM is committed
    // (double rAF) since the menu and the Delegate/Reopen buttons mount or swap with
    // their phases.
    useEffect(() => {
        if (isPlan) {
            return undefined;
        }
        const moveTo = (ref, duration) => {
            const appRect = appRef.current?.getBoundingClientRect();
            const target = ref.current?.getBoundingClientRect();
            if (!appRect || !target) return;
            setCursor({
                x: target.left - appRect.left + target.width / 2,
                y: target.top - appRect.top + target.height / 2,
                instant: false,
                shown: true,
                duration,
            });
        };
        const park = (fx, fy) => {
            const appRect = appRef.current?.getBoundingClientRect();
            if (!appRect) return;
            setCursor({ x: appRect.width * fx, y: appRect.height * fy, instant: true, shown: false, duration: 1000 });
        };
        let raf1;
        let raf2;
        const afterCommit = (fn) => {
            raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(fn);
            });
        };
        if (phase === 'a-move') {
            afterCommit(() => moveTo(moveBtnRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'a-menu') {
            afterCommit(() => moveTo(menuItemRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'd-omove') {
            afterCommit(() => moveTo(reopenRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'h-move') {
            afterCommit(() => moveTo(pokeAiRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'g-addmove') {
            afterCommit(() => moveTo(approvalAddRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'g-certmove') {
            afterCommit(() => moveTo(approvalCertaintyRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'g-submitmove') {
            afterCommit(() => moveTo(approvalSubmitRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'i-marketing-move') {
            afterCommit(() => moveTo(inboxMarketingRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'i-choice-move') {
            afterCommit(() => moveTo(inboxChoiceRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'i-certainty-move') {
            afterCommit(() => moveTo(inboxCertaintyRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'i-approve-move') {
            afterCommit(() => moveTo(inboxMarketingSubmitRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'i-engineering-move') {
            afterCommit(() => moveTo(inboxEngineeringRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'i-reply-move') {
            afterCommit(() => moveTo(inboxReplyReportRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'i-submit-move') {
            afterCommit(() => moveTo(inboxSubmitRef, scenePhases[phaseIndex].ms - 80));
        } else if (phase === 'a-view' || phase === 'a-suggest') {
            // Park hidden at a resting spot so the a-move glide has a start point —
            // the same happens before the reopen act.
            afterCommit(() => park(0.7, 0.8));
        } else if (phase === 'd-resolved') {
            afterCommit(() => park(0.75, 0.85));
        } else if (phase === 'h-job') {
            afterCommit(() => park(0.18, 0.82));
        } else if (phase === 'g-job') {
            afterCommit(() => park(0.2, 0.6));
        } else if (phase === 'i-inbox') {
            afterCommit(() => park(0.22, 0.78));
        } else if (['a-bug', 'd-reopen', 'c-chat', 'b-question', 'e-ask', 'f-ask',
            'g-wizard', 'g-reason', 'g-done', 'h-sent', 'h-question', 'h-reply',
            'h-replytab', 'h-replyenter', 'h-replysent', 'h-terminal',
            'h-continue', 'i-marketing-question', 'i-vote', 'i-choice-reason',
            'i-marketing-done', 'i-review', 'i-feedback', 'i-done',
            't-ask', 't-report'].includes(phase)) {
            setCursor((prev) => ({ ...prev, shown: false }));
        }
        if (['a-move', 'a-open', 'a-menu', 'a-click', 'd-omove', 'd-oclick',
            'g-addmove', 'g-addclick', 'g-certmove', 'g-certclick',
            'g-submitmove', 'g-submitclick', 'h-move', 'h-click',
            'i-marketing-move', 'i-marketing-open', 'i-choice-move', 'i-choice-click',
            'i-certainty-move', 'i-certainty-click', 'i-approve-move', 'i-approve-click',
            'i-engineering-move', 'i-engineering-open', 'i-submit-move',
            'i-submit-click', 'i-reply-move', 'i-reply-click'].includes(phase)) {
            setCursor((prev) => ({ ...prev, shown: true }));
        }
        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, phaseIndex, loopCount]);

    const clicking = ['a-open', 'a-click', 'd-oclick', 'g-addclick',
        'g-certclick', 'g-submitclick', 'h-click', 'i-marketing-open',
        'i-choice-click', 'i-certainty-click', 'i-approve-click',
        'i-engineering-open', 'i-reply-click', 'i-submit-click'].includes(phase);
    const isPokeTerminal = scene === 'h' && ['h-terminal', 'h-continue'].includes(phase);
    const isChat = scene === 'c' || scene === 'e' || scene === 'f' || scene === 't' || isPokeTerminal;

    return (
        <Container>
            <WideWrap>
                <SyncGrid>
                    {isMobile ? (
                        <MobileCaption onClick={advanceFeature}>
                            {FEATURES.find((feature) => feature.id === selectedId).label}
                        </MobileCaption>
                    ) : (
                        <FeatureCol>
                            {FEATURES.map((feature) => (
                                <FeatureItem key={feature.id} $active={feature.id === selectedId}
                                    onClick={() => selectFeature(feature)}>
                                    {feature.label}
                                </FeatureItem>
                            ))}
                        </FeatureCol>
                    )}
                    <AnimCol>
                        {isPlan ? (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <PlanAnimation isMobile={isMobile} />
                            </div>
                        ) : (
                        <Backdrop>
                            <AppWindow ref={appRef} $chat={isChat}>
                                {isChat ? (
                                    <ChatChrome>
                                        <ClaudeMark>✳</ClaudeMark>
                                        <ChatTitle>{isPokeTerminal ? 'Your AI terminal' : 'Your AI agent'}</ChatTitle>
                                    </ChatChrome>
                                ) : scene === 'g' || scene === 'i' ? (
                                    <TopBar>
                                        <LogoImg src={logo} alt="Uclusion" />
                                        <HomeLink>Home</HomeLink>
                                        <SearchPill>
                                            <SearchGlyph />
                                            Search David Israel
                                        </SearchPill>
                                        <TopBarSpacer />
                                        <NextMsg>Next message <span>→</span></NextMsg>
                                        <TopBarRight>
                                            <TopIcon>?</TopIcon>
                                            <ModeIcon titleAccess="Light / dark mode" />
                                            <HeadAvatar bg="#4c5b8a">DI</HeadAvatar>
                                        </TopBarRight>
                                    </TopBar>
                                ) : (
                                    <TopBar>
                                        <LogoImg src={logo} alt="Uclusion" />
                                        <TopBarSpacer />
                                        <HeadAvatar bg="#4c5b8a">DI</HeadAvatar>
                                        <HeadAvatar bg={BLUE} overlap>AI</HeadAvatar>
                                    </TopBar>
                                )}
                                <Stage>
                                    {/* Hidden copies of every scene at their fullest set the height. */}
                                    <HeightGhost aria-hidden>
                                        <div><SceneA phase="a-bug" moveBtnRef={{ current: null }} menuItemRef={{ current: null }} /></div>
                                        <div><SceneB phase="d-omove" reopenRef={{ current: null }}
                                            questionId="question-options-q-12-ghost" /></div>
                                        <div><SceneC phase="c-pick" /></div>
                                        <div><SceneH phase="h-replysent" pokeRef={{ current: null }} /></div>
                                        <div><SceneH phase="h-continue" pokeRef={{ current: null }} /></div>
                                        <div><SceneE phase="e-detail" /></div>
                                        <div><SceneF phase="f-found" /></div>
                                        <div><SceneT phase="t-report" /></div>
                                        <div><SceneI phase="i-inbox" marketingRef={{ current: null }}
                                            choiceRef={{ current: null }} certaintyRef={{ current: null }}
                                            marketingSubmitRef={{ current: null }} engineeringRef={{ current: null }}
                                            replyReportRef={{ current: null }} submitRef={{ current: null }} /></div>
                                        <div><SceneI phase="i-approve-move" marketingRef={{ current: null }}
                                            choiceRef={{ current: null }} certaintyRef={{ current: null }}
                                            marketingSubmitRef={{ current: null }} engineeringRef={{ current: null }}
                                            replyReportRef={{ current: null }} submitRef={{ current: null }} /></div>
                                        <div><SceneI phase="i-submit-move" marketingRef={{ current: null }}
                                            choiceRef={{ current: null }} certaintyRef={{ current: null }}
                                            marketingSubmitRef={{ current: null }} engineeringRef={{ current: null }}
                                            replyReportRef={{ current: null }} submitRef={{ current: null }} /></div>
                                        <div><SceneG phase="g-done" addRef={{ current: null }}
                                            certaintyRef={{ current: null }} submitRef={{ current: null }} /></div>
                                        <div><SceneG phase="g-wizard" addRef={{ current: null }}
                                            certaintyRef={{ current: null }} submitRef={{ current: null }} /></div>
                                    </HeightGhost>
                                    <LiveScene key={`${scene}-${loopCount}`} $chat={isChat}
                                        data-testid="feature-animation-live-scene">
                                        {scene === 'a' && (
                                            <SceneA phase={phase} moveBtnRef={moveBtnRef} menuItemRef={menuItemRef} />
                                        )}
                                        {(scene === 'b' || scene === 'd') && (
                                            <SceneB phase={phase} reopenRef={reopenRef}
                                                questionId="question-options-q-12" />
                                        )}
                                        {scene === 'c' && <SceneC phase={phase} />}
                                        {scene === 'h' && <SceneH phase={phase} pokeRef={pokeAiRef} />}
                                        {scene === 'e' && <SceneE phase={phase} />}
                                        {scene === 'f' && <SceneF phase={phase} />}
                                        {scene === 't' && <SceneT phase={phase} />}
                                        {scene === 'i' && (
                                            <SceneI phase={phase} marketingRef={inboxMarketingRef}
                                                choiceRef={inboxChoiceRef} certaintyRef={inboxCertaintyRef}
                                                marketingSubmitRef={inboxMarketingSubmitRef}
                                                engineeringRef={inboxEngineeringRef}
                                                replyReportRef={inboxReplyReportRef} submitRef={inboxSubmitRef} />
                                        )}
                                        {scene === 'g' && (
                                            <SceneG phase={phase} addRef={approvalAddRef}
                                                certaintyRef={approvalCertaintyRef} submitRef={approvalSubmitRef} />
                                        )}
                                    </LiveScene>
                                </Stage>
                                <CursorWrap x={cursor.x} y={cursor.y} instant={cursor.instant}
                                    duration={cursor.duration} $shown={cursor.shown}>
                                    <CursorRipple clicking={clicking} />
                                    <PointerGlyph />
                                </CursorWrap>
                            </AppWindow>
                        </Backdrop>
                        )}
                    </AnimCol>
                </SyncGrid>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2.5rem' }}>
                    <PrimaryButton as="a" href={getPrimaryUrl()}>Try for Free</PrimaryButton>
                </div>
            </WideWrap>
        </Container>
    );
};

export default AiCollaborationFeature;
